import React, { useState, useEffect, useMemo } from 'react';
import { 
  Users, 
  FileText, 
  ShieldCheck, 
  Search, 
  RefreshCw, 
  Crown, 
  HardDrive,
  CheckCircle2,
  AlertCircle,
  BookOpen,
  HelpCircle,
  Plus,
  Upload,
  Edit3,
  Layers,
  Zap,
  Clock,
  Trash2,
  ExternalLink,
  ChevronRight,
  FileCheck,
  CreditCard,
  Sparkles,
  BrainCircuit,
  Wand2,
  Loader2,
  PenTool,
  FileSignature,
  Newspaper,
  Flame,
  GraduationCap,
  MessageSquare
} from 'lucide-react';
import { UserProfile, PraxisPaper, Question, StudyGuideRecord, Flashcard, EssayPrompt, ConstructedResponsePrompt, BlogPostRecord, TutoringBooking } from '../types';
import {
  getAllUsersFromFirestore,
  getAllTutoringBookingsFromFirestore,
  getPraxisPapersFromFirestore,
  savePraxisPaperToFirestore,
  getQuestionsForPaperFromFirestore,
  addQuestionToPaperInFirestore,
  batchAddQuestionsToPaperInFirestore,
  getTotalQuestionsCountFromFirestore,
  getStudyGuidesFromFirestore,
  saveStudyGuideToFirestore,
  uploadStudyGuidePdfToStorage,
  getStudyGuidePdfBlobUrl,
  deleteQuestionFromFirestore,
  deletePaperFromFirestore,
  deleteStudyGuideFromFirestore,
  getFlashcardsForPaperFromFirestore,
  saveFlashcardToFirestore,
  deleteFlashcardFromFirestore,
  getEssayPromptsForPaperFromFirestore,
  saveEssayPromptToFirestore,
  deleteEssayPromptFromFirestore,
  getConstructedResponsesForPaperFromFirestore,
  saveConstructedResponseToFirestore,
  saveBatchConstructedResponsesToFirestore,
  deleteConstructedResponseFromFirestore,
  getBlogPostsFromFirestore,
  saveBlogPostToFirestore,
  deleteBlogPostFromFirestore
} from '../lib/firebase';

interface AdminDashboardProps {
  adminProfile: UserProfile;
}

/**
 * Quote-aware CSV parser that handles commas inside quotes ("..."),
 * escaped quotes (""), and multiline content cleanly.
 */
function parseCSVContent(csvText: string): string[][] {
  const rows: string[][] = [];
  let currentField = '';
  let currentRow: string[] = [];
  let inQuotes = false;

  for (let i = 0; i < csvText.length; i++) {
    const char = csvText[i];
    const nextChar = csvText[i + 1];

    if (char === '"') {
      if (inQuotes && nextChar === '"') {
        currentField += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      currentRow.push(currentField.trim());
      currentField = '';
    } else if ((char === '\r' || char === '\n') && !inQuotes) {
      if (char === '\r' && nextChar === '\n') {
        i++;
      }
      currentRow.push(currentField.trim());
      if (currentRow.some(cell => cell.length > 0)) {
        rows.push(currentRow);
      }
      currentRow = [];
      currentField = '';
    } else {
      currentField += char;
    }
  }

  if (currentField.length > 0 || currentRow.length > 0) {
    currentRow.push(currentField.trim());
    if (currentRow.some(cell => cell.length > 0)) {
      rows.push(currentRow);
    }
  }

  return rows;
}

export const AdminDashboard: React.FC<AdminDashboardProps> = ({ adminProfile }) => {
  const ADMIN_UID = 'GBQme94c03e6XCBHGQEqF2eFBrF3';

  const [users, setUsers] = useState<UserProfile[]>([]);
  const [papers, setPapers] = useState<PraxisPaper[]>([]);
  const [totalQuestionsCount, setTotalQuestionsCount] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [contentTab, setContentTab] = useState<'papers' | 'questions' | 'study' | 'flashcards' | 'essays' | 'constructedResponses' | 'performanceTasks' | 'blog' | 'users' | 'tutoringBookings'>('papers');

  const paperFormRef = React.useRef<HTMLDivElement>(null);
  const paperCodeInputRef = React.useRef<HTMLInputElement>(null);

  // Selected Paper for Questions, Flashcards, and Study Guides
  const [selectedPaperId, setSelectedPaperId] = useState<string>('5713');

  // --- PAPERS FORM STATE ---
  const [editingPaperId, setEditingPaperId] = useState<string | null>(null);
  const [paperIdInput, setPaperIdInput] = useState<string>('');
  const [paperCode, setPaperCode] = useState<string>('');
  const [paperTitle, setPaperTitle] = useState<string>('');
  const [paperCategory, setPaperCategory] = useState<'core' | 'elementary' | 'plt' | 'subject'>('core');
  const [paperContentType, setPaperContentType] = useState<'standard' | 'essay' | 'mixed' | 'constructed_response' | 'performance_assessment'>('standard');
  const [paperExamCode, setPaperExamCode] = useState<string>('Praxis-Core');
  const [paperDuration, setPaperDuration] = useState<string>('');
  const [paperPassingScore, setPaperPassingScore] = useState<string>('');
  const [paperDescription, setPaperDescription] = useState<string>('');
  const [paperDomainsText, setPaperDomainsText] = useState<string>('');
  const [paperSubmitting, setPaperSubmitting] = useState<boolean>(false);
  const [paperStatusMsg, setPaperStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- QUESTIONS FORM STATE ---
  const [editingQuestionId, setEditingQuestionId] = useState<string | null>(null);
  const [questionSubMode, setQuestionSubMode] = useState<'manual' | 'bulk' | 'ai'>('manual');
  // Manual Question
  const [qDomain, setQDomain] = useState<string>('');
  const [qText, setQText] = useState<string>('');
  const [qOption0, setQOption0] = useState<string>('');
  const [qOption1, setQOption1] = useState<string>('');
  const [qOption2, setQOption2] = useState<string>('');
  const [qOption3, setQOption3] = useState<string>('');
  const [qOption4, setQOption4] = useState<string>('');
  const [qCorrectIndex, setQCorrectIndex] = useState<number>(0);
  const [qDifficulty, setQDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [qExplanation, setQExplanation] = useState<string>('');
  const [questionSubmitting, setQuestionSubmitting] = useState<boolean>(false);
  const [questionStatusMsg, setQuestionStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Bulk / AI Question State
  const [bulkParsedQuestions, setBulkParsedQuestions] = useState<Omit<Question, 'id'>[]>([]);
  const [bulkFileName, setBulkFileName] = useState<string | null>(null);
  const [bulkError, setBulkError] = useState<string | null>(null);
  const [bulkSubmitting, setBulkSubmitting] = useState<boolean>(false);

  // AI Generator Parameters
  const [aiGenDomain, setAiGenDomain] = useState<string>('');
  const [aiGenDifficulty, setAiGenDifficulty] = useState<'easy' | 'medium' | 'hard'>('medium');
  const [aiGenCount, setAiGenCount] = useState<number>(5);
  const [aiGenerating, setAiGenerating] = useState<boolean>(false);
  const [aiGenError, setAiGenError] = useState<string | null>(null);

  // Existing Questions List State
  const [existingQuestions, setExistingQuestions] = useState<Question[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState<boolean>(false);

  // --- STUDY GUIDES FORM STATE ---
  const [guideTitle, setGuideTitle] = useState<string>('');
  const [guideSummary, setGuideSummary] = useState<string>('');
  const [guideMarkdown, setGuideMarkdown] = useState<string>('');
  const [guideFile, setGuideFile] = useState<File | null>(null);
  const [guideSubmitting, setGuideSubmitting] = useState<boolean>(false);
  const [guideStatusMsg, setGuideStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [existingGuides, setExistingGuides] = useState<StudyGuideRecord[]>([]);
  const [loadingGuides, setLoadingGuides] = useState<boolean>(false);
  const [guidePdfPreviewLoadingId, setGuidePdfPreviewLoadingId] = useState<string | null>(null);

  // --- FLASHCARDS FORM STATE ---
  const [editingCardId, setEditingCardId] = useState<string | null>(null);
  const [cardDomain, setCardDomain] = useState<string>('Core Vocabulary');
  const [cardTerm, setCardTerm] = useState<string>('');
  const [cardDefinition, setCardDefinition] = useState<string>('');
  const [cardExample, setCardExample] = useState<string>('');
  const [existingCards, setExistingCards] = useState<Flashcard[]>([]);
  const [loadingCards, setLoadingCards] = useState<boolean>(false);
  const [cardSubmitting, setCardSubmitting] = useState<boolean>(false);
  const [cardStatusMsg, setCardStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [cardGenerating, setCardGenerating] = useState<boolean>(false);
  const [cardGenError, setCardGenError] = useState<string | null>(null);

  // --- ESSAY PROMPTS FORM STATE ---
  const [editingPromptId, setEditingPromptId] = useState<string | null>(null);
  const [promptType, setPromptType] = useState<'argumentative' | 'informative-explanatory'>('argumentative');
  const [promptTitle, setPromptTitle] = useState<string>('');
  const [promptText, setPromptText] = useState<string>('');
  const [promptTimeLimit, setPromptTimeLimit] = useState<number>(30);
  const [promptSample6, setPromptSample6] = useState<string>('');
  const [promptRubricNotes, setPromptRubricNotes] = useState<string>('');
  const [existingPrompts, setExistingPrompts] = useState<EssayPrompt[]>([]);
  const [loadingPrompts, setLoadingPrompts] = useState<boolean>(false);
  const [promptSubmitting, setPromptSubmitting] = useState<boolean>(false);
  const [promptStatusMsg, setPromptStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // --- CONSTRUCTED RESPONSE PROMPTS FORM STATE ---
  const [editingCrId, setEditingCrId] = useState<string | null>(null);
  const [crDomain, setCrDomain] = useState<string>('');
  const [crPromptText, setCrPromptText] = useState<string>('');
  const [crRubricNotes, setCrRubricNotes] = useState<string>('');
  const [crTimeLimit, setCrTimeLimit] = useState<number>(10);
  const [existingCrPrompts, setExistingCrPrompts] = useState<ConstructedResponsePrompt[]>([]);
  const [loadingCrPrompts, setLoadingCrPrompts] = useState<boolean>(false);
  const [crSubmitting, setCrSubmitting] = useState<boolean>(false);
  const [crStatusMsg, setCrStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [crSubMode, setCrSubMode] = useState<'single' | 'bulk'>('single');
  const [crBulkParsed, setCrBulkParsed] = useState<(Omit<ConstructedResponsePrompt, 'id'> & { id?: string })[]>([]);
  const [crBulkFileName, setCrBulkFileName] = useState<string | null>(null);
  const [crBulkError, setCrBulkError] = useState<string | null>(null);
  const [crBulkSubmitting, setCrBulkSubmitting] = useState<boolean>(false);

  // --- BLOG POST FORM STATE ---
  const BLOG_EXAM_FAMILIES = ['CBEST', 'CSET Multiple Subjects', 'Praxis Series', 'edTPA', 'NYSTCE', 'TExES', 'FTCE', 'State Systems', 'General'];
  const [editingBlogId, setEditingBlogId] = useState<string | null>(null);
  const [blogTitle, setBlogTitle] = useState<string>('');
  const [blogSlug, setBlogSlug] = useState<string>('');
  const [blogExamFamily, setBlogExamFamily] = useState<string>(BLOG_EXAM_FAMILIES[0]);
  const [blogState, setBlogState] = useState<string>('National');
  const [blogSummary, setBlogSummary] = useState<string>('');
  const [blogImageUrl, setBlogImageUrl] = useState<string>('');
  const [blogContentMarkdown, setBlogContentMarkdown] = useState<string>('');
  const [blogReadTime, setBlogReadTime] = useState<string>('5 min read');
  const [blogTagsText, setBlogTagsText] = useState<string>('');
  const [blogStatus, setBlogStatus] = useState<'draft' | 'published'>('draft');
  const [blogSubmitting, setBlogSubmitting] = useState<boolean>(false);
  const [blogStatusMsg, setBlogStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [existingBlogPosts, setExistingBlogPosts] = useState<BlogPostRecord[]>([]);
  const [loadingBlogPosts, setLoadingBlogPosts] = useState<boolean>(false);

  // --- REGISTERED USERS TAB STATE ---
  const [userSearchQuery, setUserSearchQuery] = useState<string>('');

  // --- TUTORING BOOKINGS TAB STATE ---
  const [tutoringBookings, setTutoringBookings] = useState<TutoringBooking[]>([]);
  const [loadingTutoringBookings, setLoadingTutoringBookings] = useState<boolean>(false);
  const [tutoringBookingsFilter, setTutoringBookingsFilter] = useState<'all' | 'session' | 'consultation'>('all');

  // Helper to get active parent paper's registered domain names
  const activeSelectedPaper = (papers || []).find(p => p?.id === selectedPaperId || p?.code === selectedPaperId);
  const activePaperDomains: string[] = useMemo(() => {
    if (!activeSelectedPaper || !Array.isArray(activeSelectedPaper.domains) || activeSelectedPaper.domains.length === 0) {
      return ['General'];
    }
    return activeSelectedPaper.domains.map(d => typeof d === 'string' ? d : (d?.name || '')).filter(Boolean);
  }, [activeSelectedPaper]);

  // Client-side filtered Registered Users list (name / email / uid substring match)
  const filteredUsers: UserProfile[] = useMemo(() => {
    const q = userSearchQuery.trim().toLowerCase();
    if (!q) return users || [];
    return (users || []).filter(u =>
      (u?.displayName || '').toLowerCase().includes(q) ||
      (u?.email || '').toLowerCase().includes(q) ||
      (u?.uid || '').toLowerCase().includes(q)
    );
  }, [users, userSearchQuery]);

  // Client-side filtered Tutoring Bookings list (Paid Sessions vs Consultation Requests)
  const filteredTutoringBookings: TutoringBooking[] = useMemo(() => {
    if (tutoringBookingsFilter === 'all') return tutoringBookings || [];
    if (tutoringBookingsFilter === 'session') {
      return (tutoringBookings || []).filter(b => (b.requestType || 'session') === 'session');
    }
    return (tutoringBookings || []).filter(b => b.requestType === 'consultation');
  }, [tutoringBookings, tutoringBookingsFilter]);

  // Keep manual and AI generator default domain in sync with selected paper
  useEffect(() => {
    if (Array.isArray(activePaperDomains) && activePaperDomains.length > 0) {
      if (!activePaperDomains.includes(qDomain)) {
        setQDomain(activePaperDomains[0]);
      }
      if (!activePaperDomains.includes(aiGenDomain)) {
        setAiGenDomain(activePaperDomains[0]);
      }
    }
  }, [selectedPaperId, activePaperDomains, qDomain, aiGenDomain]);

  // Load Main Stats & Papers
  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [fetchedUsers, fetchedPapers, qCount] = await Promise.all([
        getAllUsersFromFirestore(),
        getPraxisPapersFromFirestore(),
        getTotalQuestionsCountFromFirestore()
      ]);

      let combinedUsers = [...fetchedUsers];
      if (!combinedUsers.some(u => u.uid === adminProfile.uid)) {
        combinedUsers.unshift(adminProfile);
      }

      setUsers(combinedUsers);
      setPapers(fetchedPapers);
      setTotalQuestionsCount(qCount);

      if (fetchedPapers.length > 0 && !selectedPaperId) {
        setSelectedPaperId(fetchedPapers[0].id);
      }
    } catch (err) {
      console.error('Error fetching admin content manager data:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load Questions whenever selectedPaperId changes or after question submit
  const fetchQuestionsForPaper = async (pId: string) => {
    setLoadingQuestions(true);
    try {
      const qs = await getQuestionsForPaperFromFirestore(pId);
      setExistingQuestions(qs);
    } catch (err) {
      console.error('Error loading questions:', err);
    } finally {
      setLoadingQuestions(false);
    }
  };

  // Load Study Guides
  const fetchGuidesForPaper = async (pId: string) => {
    setLoadingGuides(true);
    try {
      const g = await getStudyGuidesFromFirestore(pId);
      setExistingGuides(g);
    } catch (err) {
      console.error('Error loading study guides:', err);
    } finally {
      setLoadingGuides(false);
    }
  };

  // Load Flashcards
  const fetchCardsForPaper = async (pId: string) => {
    setLoadingCards(true);
    try {
      const fc = await getFlashcardsForPaperFromFirestore(pId);
      setExistingCards(fc);
    } catch (err) {
      console.error('Error loading flashcards:', err);
    } finally {
      setLoadingCards(false);
    }
  };

  // Load Essay Prompts
  const fetchPromptsForPaper = async (pId: string) => {
    setLoadingPrompts(true);
    try {
      const eps = await getEssayPromptsForPaperFromFirestore(pId);
      setExistingPrompts(eps);
    } catch (err) {
      console.error('Error loading essay prompts:', err);
    } finally {
      setLoadingPrompts(false);
    }
  };

  // Load Constructed Response Prompts
  const fetchCrPromptsForPaper = async (pId: string) => {
    setLoadingCrPrompts(true);
    try {
      const crs = await getConstructedResponsesForPaperFromFirestore(pId);
      setExistingCrPrompts(crs);
    } catch (err) {
      console.error('Error loading constructed response prompts:', err);
    } finally {
      setLoadingCrPrompts(false);
    }
  };

  // Load Blog Posts (global, not tied to any specific paper)
  const fetchBlogPosts = async () => {
    setLoadingBlogPosts(true);
    try {
      const posts = await getBlogPostsFromFirestore(true);
      setExistingBlogPosts(posts);
    } catch (err) {
      console.error('Error loading blog posts:', err);
    } finally {
      setLoadingBlogPosts(false);
    }
  };

  // Load Tutoring Bookings (global, not tied to any specific paper)
  const fetchTutoringBookings = async () => {
    setLoadingTutoringBookings(true);
    try {
      const bookings = await getAllTutoringBookingsFromFirestore();
      setTutoringBookings(bookings);
    } catch (err) {
      console.error('Error loading tutoring bookings:', err);
    } finally {
      setLoadingTutoringBookings(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchBlogPosts();
    fetchTutoringBookings();
  }, [adminProfile]);

  useEffect(() => {
    if (selectedPaperId) {
      fetchQuestionsForPaper(selectedPaperId);
      fetchGuidesForPaper(selectedPaperId);
      fetchCardsForPaper(selectedPaperId);
      fetchPromptsForPaper(selectedPaperId);
      fetchCrPromptsForPaper(selectedPaperId);
    }
  }, [selectedPaperId]);

  // --- PAPER HANDLERS ---
  const handleSelectPaperForEdit = (paper: PraxisPaper) => {
    setEditingPaperId(paper.id);
    setPaperIdInput(paper.id);
    setPaperCode(paper.code);
    setPaperTitle(paper.title);
    setPaperCategory(paper.category);
    setPaperContentType(paper.contentType || 'standard');
    setPaperExamCode(paper.examCode || (paper.category === 'core' ? 'Praxis-Core' : paper.category === 'plt' ? 'Praxis-PLT' : 'Praxis-Subject'));
    setPaperDuration(paper.durationMinutes ? String(paper.durationMinutes) : '');
    setPaperPassingScore(paper.passingScoreEts ? String(paper.passingScoreEts) : '');
    setPaperDescription(paper.description || '');
    setPaperDomainsText(paper.domains?.map(d => `${d.name} (${d.weightPercentage}%)`).join(', ') || '');
    setPaperStatusMsg(null);

    // Smoothly scroll up and focus the paper editing form
    setTimeout(() => {
      paperFormRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      paperCodeInputRef.current?.focus();
    }, 50);
  };

  const handleResetPaperForm = () => {
    setEditingPaperId(null);
    setPaperIdInput('');
    setPaperCode('');
    setPaperTitle('');
    setPaperCategory('core');
    setPaperContentType('standard');
    setPaperExamCode('Praxis-Core');
    setPaperDuration('');
    setPaperPassingScore('');
    setPaperDescription('');
    setPaperDomainsText('');
    setPaperStatusMsg(null);
  };

  const handleSavePaper = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!paperCode.trim() || !paperTitle.trim()) {
      setPaperStatusMsg({ type: 'error', text: 'Paper Code and Title are required.' });
      return;
    }

    setPaperSubmitting(true);
    setPaperStatusMsg(null);

    try {
      const targetId = editingPaperId || paperIdInput.trim() || paperCode.trim();
      const domainsParsed = paperDomainsText.trim().length > 0
        ? paperDomainsText.split(',').map(dStr => {
            const match = dStr.match(/(.+)\((\d+)%\)/);
            if (match) {
              return { name: match[1].trim(), weightPercentage: parseInt(match[2], 10) || 25 };
            }
            return { name: dStr.trim(), weightPercentage: 25 };
          }).filter(d => d.name.length > 0)
        : [];

      await savePraxisPaperToFirestore({
        id: targetId,
        code: paperCode.trim(),
        title: paperTitle.trim(),
        category: paperCategory,
        contentType: paperContentType,
        examCode: paperExamCode.trim() || 'Praxis-Core',
        durationMinutes: paperDuration.trim() !== '' ? (parseInt(paperDuration, 10) || 0) : 0,
        passingScoreEts: paperPassingScore.trim() !== '' ? (parseInt(paperPassingScore, 10) || null) : null,
        description: paperDescription.trim(),
        domains: domainsParsed.length > 0 ? domainsParsed : [{ name: 'General', weightPercentage: 100 }]
      });

      setPaperStatusMsg({ type: 'success', text: `Successfully saved Paper ${paperCode} to Firestore!` });
      handleResetPaperForm();
      fetchDashboardData();
    } catch (err: any) {
      setPaperStatusMsg({ type: 'error', text: err.message || 'Failed to save paper.' });
    } finally {
      setPaperSubmitting(false);
    }
  };

  const handleDeletePaper = async (pId: string, title: string) => {
    if (!window.confirm(`Are you sure you want to delete paper "${title}" (${pId}) from Firestore?`)) return;
    try {
      await deletePaperFromFirestore(pId);
      if (selectedPaperId === pId) {
        setSelectedPaperId('');
      }
      fetchDashboardData();
    } catch (err: any) {
      alert(`Error deleting paper: ${err.message}`);
    }
  };

  // --- MANUAL QUESTION HANDLER ---
  const handleEditQuestionClick = (q: Question) => {
    setEditingQuestionId(q.id);
    setQDomain(q.domain || '');
    setQText(q.questionText || '');
    setQOption0(q.options?.[0] || '');
    setQOption1(q.options?.[1] || '');
    setQOption2(q.options?.[2] || '');
    setQOption3(q.options?.[3] || '');
    setQOption4(q.options?.[4] || '');
    setQCorrectIndex(q.correctAnswerIndex ?? 0);
    setQDifficulty(q.difficulty || 'medium');
    setQExplanation(q.explanation || '');
    setQuestionSubMode('manual');
    setQuestionStatusMsg(null);
  };

  const handleResetQuestionForm = () => {
    setEditingQuestionId(null);
    setQDomain('');
    setQText('');
    setQOption0('');
    setQOption1('');
    setQOption2('');
    setQOption3('');
    setQOption4('');
    setQCorrectIndex(0);
    setQDifficulty('medium');
    setQExplanation('');
    setQuestionStatusMsg(null);
  };

  const handleSaveManualQuestion = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!qText.trim() || !qOption0.trim() || !qOption1.trim()) {
      setQuestionStatusMsg({ type: 'error', text: 'Question text and at least 2 options are required.' });
      return;
    }

    setQuestionSubmitting(true);
    setQuestionStatusMsg(null);

    try {
      const optionsArray = [qOption0, qOption1, qOption2, qOption3, qOption4].filter(o => o.trim().length > 0);

      await addQuestionToPaperInFirestore(selectedPaperId, {
        paperId: selectedPaperId,
        domain: qDomain.trim() || 'General',
        questionText: qText.trim(),
        options: optionsArray,
        correctAnswerIndex: Number(qCorrectIndex),
        difficulty: qDifficulty,
        explanation: qExplanation.trim()
      });

      setQuestionStatusMsg({ type: 'success', text: editingQuestionId ? 'Question updated in Firestore!' : 'Question added successfully to Firestore!' });
      handleResetQuestionForm();

      fetchQuestionsForPaper(selectedPaperId);
      fetchDashboardData();
    } catch (err: any) {
      setQuestionStatusMsg({ type: 'error', text: err.message || 'Error saving question.' });
    } finally {
      setQuestionSubmitting(false);
    }
  };

  const handleDeleteQuestion = async (qId: string) => {
    if (!window.confirm('Delete this question permanently from Firestore?')) return;
    try {
      await deleteQuestionFromFirestore(selectedPaperId, qId);
      fetchQuestionsForPaper(selectedPaperId);
      fetchDashboardData();
    } catch (err: any) {
      alert(`Error deleting question: ${err.message}`);
    }
  };

  // --- BULK FILE PARSING HANDLER ---
  const handleFileUploadForBulk = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBulkFileName(file.name);
    setBulkError(null);
    setBulkParsedQuestions([]);

    const validDomains = activePaperDomains.length > 0 ? activePaperDomains : ['General'];
    const defaultDomain = validDomains[0];

    // Helper to validate and normalize imported domain against registered domains
    const normalizeDomain = (rawDomain: any): string => {
      if (typeof rawDomain !== 'string' || !rawDomain.trim()) return defaultDomain;
      const trimmed = rawDomain.trim();
      // Exact match
      const exactMatch = validDomains.find(d => d.toLowerCase() === trimmed.toLowerCase());
      if (exactMatch) return exactMatch;
      // Partial / inclusion match (e.g., 'Craft, Structure, and Language Skills' -> 'Craft and Structure')
      const partialMatch = validDomains.find(d => 
        trimmed.toLowerCase().includes(d.toLowerCase()) || d.toLowerCase().includes(trimmed.toLowerCase())
      );
      if (partialMatch) return partialMatch;
      // Default to registered primary domain
      return defaultDomain;
    };

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(content);
          if (!Array.isArray(parsed)) {
            throw new Error('JSON file must contain an array of question objects.');
          }

          // GUARD: Detect constructed response uploads in multiple choice pool
          if (parsed.some((item: any) => item.promptText || item.rubricNotes || item.timeLimitMinutes !== undefined)) {
            throw new Error("This file contains Constructed Response prompts (found 'promptText' or 'rubricNotes'). Please upload it in the 'Constructed Responses' tab, not the Multiple Choice Question Pool.");
          }

          const formatted: Omit<Question, 'id'>[] = parsed.map((item: any, idx: number) => {
            const qText = (item.questionText || item.question || '').trim();
            if (!qText) {
              throw new Error(`Item at index ${idx + 1} is missing a valid 'questionText' or 'question' field.`);
            }

            let opts: string[] = [];
            if (Array.isArray(item.options) && item.options.length >= 2) {
              opts = item.options.map((o: any) => String(o).trim()).filter(Boolean);
            } else if (item.option0 && item.option1) {
              opts = [item.option0, item.option1, item.option2, item.option3].filter(Boolean).map(String);
            }

            if (opts.length < 2) {
              throw new Error(`Item #${idx + 1} ("${qText.substring(0, 40)}...") is missing valid multiple-choice options array.`);
            }

            const correctIdx = typeof item.correctAnswerIndex === 'number' ? item.correctAnswerIndex : 0;
            if (correctIdx < 0 || correctIdx >= opts.length) {
              throw new Error(`Item #${idx + 1} has an invalid 'correctAnswerIndex' (${correctIdx}) for ${opts.length} options.`);
            }

            return {
              paperId: selectedPaperId,
              domain: normalizeDomain(item.domain),
              questionText: qText,
              options: opts,
              correctAnswerIndex: correctIdx,
              explanation: item.explanation || '',
              difficulty: (['easy', 'medium', 'hard'].includes(item.difficulty) ? item.difficulty : 'medium') as 'easy' | 'medium' | 'hard'
            };
          });

          setBulkParsedQuestions(formatted);
        } else if (file.name.endsWith('.csv')) {
          const rows = parseCSVContent(content);
          if (rows.length < 2) {
            throw new Error('CSV file must have a header row and at least 1 question row.');
          }
          const formatted: Omit<Question, 'id'>[] = [];
          for (let i = 1; i < rows.length; i++) {
            const parts = rows[i];
            if (parts.length >= 6) {
              const qText = (parts[1] || '').trim();
              const opts = [parts[2], parts[3], parts[4], parts[5]].map(p => (p || '').trim()).filter(Boolean);
              if (!qText || opts.length < 2) {
                throw new Error(`CSV Row #${i + 1} is missing a valid question text or options.`);
              }

              formatted.push({
                paperId: selectedPaperId,
                domain: normalizeDomain(parts[0]),
                questionText: qText,
                options: opts,
                correctAnswerIndex: parseInt(parts[6] || '0', 10) || 0,
                explanation: parts[7] || '',
                difficulty: (['easy', 'medium', 'hard'].includes(parts[8]) ? parts[8] : 'medium') as 'easy' | 'medium' | 'hard'
              });
            }
          }
          if (formatted.length === 0) {
            throw new Error('Could not parse any valid question rows from CSV.');
          }
          setBulkParsedQuestions(formatted);
        } else {
          throw new Error('Please upload a valid .json or .csv file.');
        }
      } catch (err: any) {
        setBulkError(err.message || 'Error parsing file.');
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmBulkWrite = async () => {
    if (bulkParsedQuestions.length === 0) return;
    setBulkSubmitting(true);
    setBulkError(null);

    try {
      const count = await batchAddQuestionsToPaperInFirestore(selectedPaperId, bulkParsedQuestions);
      setQuestionStatusMsg({ type: 'success', text: `Batch wrote ${count} questions to paper ${selectedPaperId}!` });
      setBulkParsedQuestions([]);
      setBulkFileName(null);
      fetchQuestionsForPaper(selectedPaperId);
      fetchDashboardData();
    } catch (err: any) {
      setBulkError(err.message || 'Batch write failed.');
    } finally {
      setBulkSubmitting(false);
    }
  };

  // --- AI QUESTION GENERATION HANDLER ---
  const handleGenerateQuestionsWithAI = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const currentPaper = papers.find(p => p.id === selectedPaperId);
    setAiGenerating(true);
    setAiGenError(null);
    setQuestionStatusMsg(null);

    try {
      const targetDomain = aiGenDomain.trim() || currentPaper?.domains?.[0]?.name || 'Core Standards & Pedagogy';

      const res = await fetch('/api/ai/generate-questions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paperCode: currentPaper?.code || selectedPaperId,
          paperTitle: currentPaper?.title || '',
          domain: targetDomain,
          difficulty: aiGenDifficulty,
          count: aiGenCount
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate practice questions with AI.');
      }

      if (!data.questions || !Array.isArray(data.questions) || data.questions.length === 0) {
        throw new Error('AI returned no valid questions.');
      }

      const formatted: Omit<Question, 'id'>[] = data.questions.map((q: any) => ({
        paperId: selectedPaperId,
        domain: q.domain || targetDomain,
        questionText: q.questionText,
        options: q.options,
        correctAnswerIndex: q.correctAnswerIndex,
        explanation: q.explanation,
        difficulty: q.difficulty || aiGenDifficulty
      }));

      setBulkParsedQuestions(formatted);
      setBulkFileName(`AI Generated: ${formatted.length} items (${aiGenDifficulty.toUpperCase()})`);
      setQuestionStatusMsg({
        type: 'success',
        text: `Gemini generated ${formatted.length} original questions! Inspect or modify the preview list below and click "Confirm & Batch-Write" to save.`
      });
    } catch (err: any) {
      setAiGenError(err.message || 'Error generating questions with AI.');
    } finally {
      setAiGenerating(false);
    }
  };

  // --- STUDY GUIDE HANDLER ---
  const handleSaveStudyGuide = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!guideTitle.trim() || !guideMarkdown.trim()) {
      setGuideStatusMsg({ type: 'error', text: 'Title and content text are required.' });
      return;
    }

    setGuideSubmitting(true);
    setGuideStatusMsg(null);

    try {
      let pdfStoragePath: string | undefined = undefined;
      let pdfName: string | undefined = undefined;

      if (guideFile) {
        // Deliberately no public download URL is generated or stored here
        // (2026-09-20 fix) -- only the private Storage path. The PDF is
        // served to students through the authenticated
        // GET /api/study-guides/:guideId/pdf endpoint, which re-checks
        // subscription/unlock status on every request instead of handing
        // out a standing, shareable link. See firebase.ts's
        // uploadStudyGuidePdfToStorage for the full explanation.
        const uploadRes = await uploadStudyGuidePdfToStorage(guideFile, selectedPaperId);
        pdfStoragePath = uploadRes.storagePath;
        pdfName = uploadRes.name;
      }

      // Auto-fill paperCode from the currently selected paper so the new
      // Study Guides tile grid (which joins guides to papers by paperId, but
      // shows paperCode as a fallback label) always has something to display
      // even if the paper lookup fails client-side.
      const currentPaper = papers.find(p => p.id === selectedPaperId);

      await saveStudyGuideToFirestore({
        paperId: selectedPaperId,
        paperCode: currentPaper?.code || selectedPaperId,
        title: guideTitle.trim(),
        summary: guideSummary.trim() || undefined,
        contentMarkdown: guideMarkdown.trim(),
        pdfStoragePath,
        pdfName,
        updatedAt: new Date().toISOString()
      });

      setGuideStatusMsg({ type: 'success', text: 'Study guide saved successfully!' });
      setGuideTitle('');
      setGuideSummary('');
      setGuideMarkdown('');
      setGuideFile(null);
      fetchGuidesForPaper(selectedPaperId);
    } catch (err: any) {
      setGuideStatusMsg({ type: 'error', text: err.message || 'Failed to save study guide.' });
    } finally {
      setGuideSubmitting(false);
    }
  };

  const handleDeleteGuide = async (guideId: string) => {
    if (!window.confirm('Delete this study guide permanently from Firestore?')) return;
    try {
      await deleteStudyGuideFromFirestore(guideId);
      fetchGuidesForPaper(selectedPaperId);
    } catch (err: any) {
      alert(`Error deleting study guide: ${err.message}`);
    }
  };

  // Opens a guide's PDF via the authenticated endpoint (never a public
  // Storage link, see firebase.ts's getStudyGuidePdfBlobUrl / the
  // 2026-09-20 handover entry) so the admin's own preview button here goes
  // through the same subscription-safe path every student's viewer does.
  const handlePreviewGuidePdf = async (guideId: string) => {
    setGuidePdfPreviewLoadingId(guideId);
    try {
      const blobUrl = await getStudyGuidePdfBlobUrl(guideId);
      window.open(blobUrl, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      setGuideStatusMsg({ type: 'error', text: err.message || 'Failed to open PDF preview.' });
    } finally {
      setGuidePdfPreviewLoadingId(null);
    }
  };

  // --- BLOG POST HANDLERS ---
  const slugify = (text: string): string =>
    text
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');

  const handleResetBlogForm = () => {
    setEditingBlogId(null);
    setBlogTitle('');
    setBlogSlug('');
    setBlogExamFamily(BLOG_EXAM_FAMILIES[0]);
    setBlogState('National');
    setBlogSummary('');
    setBlogImageUrl('');
    setBlogContentMarkdown('');
    setBlogReadTime('5 min read');
    setBlogTagsText('');
    setBlogStatus('draft');
    setBlogStatusMsg(null);
  };

  const handleEditBlogClick = (post: BlogPostRecord) => {
    setEditingBlogId(post.id);
    setBlogTitle(post.title);
    setBlogSlug(post.slug);
    setBlogExamFamily(post.examFamily || BLOG_EXAM_FAMILIES[0]);
    setBlogState(post.state || 'National');
    setBlogSummary(post.summary);
    setBlogImageUrl(post.imageUrl || '');
    setBlogContentMarkdown(post.contentMarkdown || '');
    setBlogReadTime(post.readTime || '5 min read');
    setBlogTagsText((post.tags || []).join(', '));
    setBlogStatus(post.status || 'draft');
    setBlogStatusMsg(null);
  };

  const handleSaveBlogPost = async (e: React.FormEvent, publishNow?: boolean) => {
    e.preventDefault();
    if (!blogTitle.trim() || !blogSummary.trim()) {
      setBlogStatusMsg({ type: 'error', text: 'Title and summary are required.' });
      return;
    }

    setBlogSubmitting(true);
    setBlogStatusMsg(null);

    try {
      const slug = (blogSlug.trim() || slugify(blogTitle));
      const tags = blogTagsText.split(',').map(t => t.trim()).filter(Boolean);
      const status = publishNow ? 'published' : blogStatus;

      await saveBlogPostToFirestore({
        id: editingBlogId || undefined,
        title: blogTitle.trim(),
        slug,
        examFamily: blogExamFamily,
        examCode: blogExamFamily,
        state: blogState.trim() || 'National',
        summary: blogSummary.trim(),
        imageUrl: blogImageUrl.trim(),
        contentMarkdown: blogContentMarkdown.trim(),
        readTime: blogReadTime.trim() || '5 min read',
        tags,
        status,
        publishedDate: new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });

      setBlogStatusMsg({ type: 'success', text: status === 'published' ? 'Blog post published to the live site!' : 'Draft saved.' });
      handleResetBlogForm();
      fetchBlogPosts();
    } catch (err: any) {
      setBlogStatusMsg({ type: 'error', text: err.message || 'Failed to save blog post.' });
    } finally {
      setBlogSubmitting(false);
    }
  };

  const handleDeleteBlogPost = async (postId: string) => {
    if (!window.confirm('Delete this blog post permanently? It will immediately disappear from the live site.')) return;
    try {
      await deleteBlogPostFromFirestore(postId);
      if (editingBlogId === postId) handleResetBlogForm();
      fetchBlogPosts();
    } catch (err: any) {
      alert(`Error deleting blog post: ${err.message}`);
    }
  };

  const handleToggleBlogPublish = async (post: BlogPostRecord) => {
    try {
      await saveBlogPostToFirestore({
        id: post.id,
        title: post.title,
        slug: post.slug,
        examFamily: post.examFamily,
        examCode: post.examCode,
        state: post.state,
        summary: post.summary,
        imageUrl: post.imageUrl,
        contentMarkdown: post.contentMarkdown,
        readTime: post.readTime,
        tags: post.tags,
        status: post.status === 'published' ? 'draft' : 'published',
        publishedDate: post.publishedDate || new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
      });
      fetchBlogPosts();
    } catch (err: any) {
      alert(`Error updating blog post status: ${err.message}`);
    }
  };

  // --- FLASHCARD HANDLERS ---
  const handleResetCardForm = () => {
    setEditingCardId(null);
    setCardTerm('');
    setCardDefinition('');
    setCardExample('');
    setCardStatusMsg(null);
  };

  const handleEditCardClick = (fc: Flashcard) => {
    setEditingCardId(fc.id);
    setCardDomain(fc.domain || 'Core Vocabulary');
    setCardTerm(fc.term);
    setCardDefinition(fc.definition);
    setCardExample(fc.exampleScenario || '');
    setCardStatusMsg(null);
  };

  const handleSaveFlashcard = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!cardTerm.trim() || !cardDefinition.trim()) {
      setCardStatusMsg({ type: 'error', text: 'Term and Definition are required.' });
      return;
    }

    setCardSubmitting(true);
    setCardStatusMsg(null);

    try {
      await saveFlashcardToFirestore(selectedPaperId, {
        id: editingCardId || undefined,
        paperId: selectedPaperId,
        domain: cardDomain.trim() || 'Core Vocabulary',
        term: cardTerm.trim(),
        definition: cardDefinition.trim(),
        exampleScenario: cardExample.trim()
      });

      setCardStatusMsg({ type: 'success', text: editingCardId ? 'Flashcard updated!' : 'Flashcard added to Firestore!' });
      handleResetCardForm();
      fetchCardsForPaper(selectedPaperId);
    } catch (err: any) {
      setCardStatusMsg({ type: 'error', text: err.message || 'Failed to save flashcard.' });
    } finally {
      setCardSubmitting(false);
    }
  };

  const handleDeleteFlashcard = async (cardId: string) => {
    if (!window.confirm('Delete this flashcard permanently from Firestore?')) return;
    try {
      await deleteFlashcardFromFirestore(selectedPaperId, cardId);
      fetchCardsForPaper(selectedPaperId);
    } catch (err: any) {
      alert(`Error deleting flashcard: ${err.message}`);
    }
  };

  // --- AI FLASHCARD GENERATION HANDLER (grounded in this paper's Question Pool) ---
  const handleGenerateFlashcardsFromQuestionPool = async () => {
    if (!existingQuestions || existingQuestions.length === 0) {
      setCardGenError("Load or add questions to this paper's Question Pool first -- Gemini grounds flashcards in the real question content.");
      return;
    }

    const currentPaper = papers.find(p => p.id === selectedPaperId);
    setCardGenerating(true);
    setCardGenError(null);
    setCardStatusMsg(null);

    try {
      const res = await fetch('/api/flashcards/generate-shared', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paperCode: currentPaper?.code || selectedPaperId,
          paperTitle: currentPaper?.title || '',
          examCode: currentPaper?.examCode || '',
          domain: cardDomain.trim() || existingQuestions[0]?.domain || 'Core Vocabulary',
          questions: existingQuestions,
          count: 10
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to generate flashcards with AI.');
      }

      if (!data.flashcards || !Array.isArray(data.flashcards) || data.flashcards.length === 0) {
        throw new Error('AI returned no valid flashcards.');
      }

      for (const fc of data.flashcards) {
        await saveFlashcardToFirestore(selectedPaperId, {
          paperId: selectedPaperId,
          domain: fc.domain || cardDomain.trim() || 'Core Vocabulary',
          term: fc.term,
          definition: fc.definition,
          exampleScenario: fc.exampleScenario || '',
          scope: 'shared',
          generatedBy: 'gemini'
        });
      }

      setCardStatusMsg({ type: 'success', text: `Gemini generated and saved ${data.flashcards.length} new flashcards from this paper's question pool!` });
      fetchCardsForPaper(selectedPaperId);
    } catch (err: any) {
      setCardGenError(err.message || 'Error generating flashcards with AI.');
    } finally {
      setCardGenerating(false);
    }
  };

  // --- ESSAY PROMPT HANDLERS ---
  const handleResetPromptForm = () => {
    setEditingPromptId(null);
    setPromptType('argumentative');
    setPromptTitle('');
    setPromptText('');
    setPromptTimeLimit(30);
    setPromptSample6('');
    setPromptRubricNotes('');
    setPromptStatusMsg(null);
  };

  const handleEditPromptClick = (ep: EssayPrompt) => {
    setEditingPromptId(ep.id);
    setPromptType(ep.type || 'argumentative');
    setPromptTitle(ep.title);
    setPromptText(ep.promptText);
    setPromptTimeLimit(ep.timeLimitMinutes || 30);
    setPromptSample6(ep.sample6Response || '');
    setPromptRubricNotes(ep.rubricNotes || '');
    setPromptStatusMsg(null);
  };

  const handleSaveEssayPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!promptTitle.trim() || !promptText.trim()) {
      setPromptStatusMsg({ type: 'error', text: 'Prompt Title and Prompt Stimulus Text are required.' });
      return;
    }

    setPromptSubmitting(true);
    setPromptStatusMsg(null);

    try {
      await saveEssayPromptToFirestore(selectedPaperId, {
        id: editingPromptId || undefined,
        paperId: selectedPaperId,
        type: promptType,
        title: promptTitle.trim(),
        promptText: promptText.trim(),
        timeLimitMinutes: Number(promptTimeLimit) || 30,
        sample6Response: promptSample6.trim(),
        rubricNotes: promptRubricNotes.trim()
      });

      setPromptStatusMsg({ type: 'success', text: editingPromptId ? 'Essay prompt updated in Firestore!' : 'Essay prompt saved to Firestore!' });
      handleResetPromptForm();
      fetchPromptsForPaper(selectedPaperId);
    } catch (err: any) {
      setPromptStatusMsg({ type: 'error', text: err.message || 'Failed to save essay prompt.' });
    } finally {
      setPromptSubmitting(false);
    }
  };

  const handleDeleteEssayPrompt = async (promptId: string) => {
    if (!window.confirm('Delete this essay prompt permanently from Firestore?')) return;
    try {
      await deleteEssayPromptFromFirestore(selectedPaperId, promptId);
      fetchPromptsForPaper(selectedPaperId);
    } catch (err: any) {
      alert(`Error deleting essay prompt: ${err.message}`);
    }
  };

  // --- CONSTRUCTED RESPONSE PROMPT HANDLERS ---
  const handleResetCrForm = () => {
    setEditingCrId(null);
    setCrDomain(activePaperDomains[0] || 'General');
    setCrPromptText('');
    setCrRubricNotes('');
    setCrTimeLimit(10);
    setCrStatusMsg(null);
  };

  const handleEditCrClick = (cr: ConstructedResponsePrompt) => {
    setEditingCrId(cr.id);
    setCrDomain(cr.domain || activePaperDomains[0] || 'General');
    setCrPromptText(cr.promptText);
    setCrRubricNotes(cr.rubricNotes || '');
    setCrTimeLimit(cr.timeLimitMinutes || 10);
    setCrStatusMsg(null);
    setCrSubMode('single');
  };

  const handleFileUploadForCrBulk = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCrBulkFileName(file.name);
    setCrBulkError(null);
    setCrBulkParsed([]);

    const validDomains = activePaperDomains.length > 0 ? activePaperDomains : ['General'];
    const defaultDomain = validDomains[0] || 'General';

    const normalizeDomain = (rawDomain: any): string => {
      if (typeof rawDomain !== 'string' || !rawDomain.trim()) return defaultDomain;
      const trimmed = rawDomain.trim();
      const exactMatch = validDomains.find(d => d.toLowerCase() === trimmed.toLowerCase());
      if (exactMatch) return exactMatch;
      const partialMatch = validDomains.find(d => 
        trimmed.toLowerCase().includes(d.toLowerCase()) || d.toLowerCase().includes(trimmed.toLowerCase())
      );
      if (partialMatch) return partialMatch;
      return defaultDomain;
    };

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        if (file.name.endsWith('.json')) {
          const parsed = JSON.parse(content);
          let items: any[] = [];
          if (Array.isArray(parsed)) {
            items = parsed;
          } else if (parsed && Array.isArray(parsed.constructedResponsePrompts)) {
            items = parsed.constructedResponsePrompts;
          } else if (parsed && Array.isArray(parsed.prompts)) {
            items = parsed.prompts;
          } else {
            throw new Error("JSON file must be an array of prompt objects or an object containing a 'constructedResponsePrompts' array.");
          }

          if (items.length === 0) {
            throw new Error('No prompt objects found in JSON file.');
          }

          const formatted: (Omit<ConstructedResponsePrompt, 'id'> & { id?: string })[] = items.map((item: any, idx: number) => {
            const pText = (item.promptText || item.prompt || item.scenario || item.task || '').trim();
            if (!pText) {
              throw new Error(`Constructed response #${idx + 1} is missing a required 'promptText' field.`);
            }

            return {
              id: item.id || undefined,
              paperId: selectedPaperId,
              domain: normalizeDomain(item.domain),
              promptText: pText,
              rubricNotes: (item.rubricNotes || item.rubric || item.scoringGuide || '').trim(),
              timeLimitMinutes: Number(item.timeLimitMinutes) || 10
            };
          });

          setCrBulkParsed(formatted);
        } else if (file.name.endsWith('.csv')) {
          const rows = parseCSVContent(content);
          if (rows.length < 2) {
            throw new Error('CSV file must have a header row and at least 1 constructed response row.');
          }
          const formatted: (Omit<ConstructedResponsePrompt, 'id'> & { id?: string })[] = [];
          for (let i = 1; i < rows.length; i++) {
            const parts = rows[i];
            if (parts.length >= 2) {
              const dom = parts[0];
              const pText = (parts[1] || '').trim();
              const rubric = parts[2] || '';
              const tLimit = Number(parts[3]) || 10;
              if (pText) {
                formatted.push({
                  paperId: selectedPaperId,
                  domain: normalizeDomain(dom),
                  promptText: pText,
                  rubricNotes: rubric.trim(),
                  timeLimitMinutes: tLimit
                });
              }
            }
          }
          if (formatted.length === 0) {
            throw new Error('Could not parse any valid constructed response rows from CSV.');
          }
          setCrBulkParsed(formatted);
        } else {
          throw new Error('Please upload a valid .json or .csv file.');
        }
      } catch (err: any) {
        setCrBulkError(err.message || 'Error parsing file.');
      }
    };
    reader.readAsText(file);
  };

  const handleConfirmCrBulkWrite = async () => {
    if (crBulkParsed.length === 0) return;
    setCrBulkSubmitting(true);
    setCrBulkError(null);

    try {
      await saveBatchConstructedResponsesToFirestore(selectedPaperId, crBulkParsed);
      setCrStatusMsg({ 
        type: 'success', 
        text: `Successfully batch imported ${crBulkParsed.length} constructed response prompt(s) to paper #${selectedPaperId}!` 
      });
      setCrBulkParsed([]);
      setCrBulkFileName(null);
      setCrSubMode('single');
      fetchCrPromptsForPaper(selectedPaperId);
      fetchDashboardData();
    } catch (err: any) {
      setCrBulkError(err.message || 'Batch save to Firestore failed.');
    } finally {
      setCrBulkSubmitting(false);
    }
  };

  const handleSaveCrPrompt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!crPromptText.trim()) {
      setCrStatusMsg({ type: 'error', text: 'Prompt text is required.' });
      return;
    }
    const targetDomain = crDomain.trim() || activePaperDomains[0] || 'General';

    setCrSubmitting(true);
    setCrStatusMsg(null);

    try {
      await saveConstructedResponseToFirestore(selectedPaperId, {
        id: editingCrId || undefined,
        paperId: selectedPaperId,
        domain: targetDomain,
        promptText: crPromptText.trim(),
        rubricNotes: crRubricNotes.trim(),
        timeLimitMinutes: Number(crTimeLimit) || 15
      });

      setCrStatusMsg({ type: 'success', text: editingCrId ? 'Constructed response updated in Firestore!' : 'Constructed response prompt saved to Firestore!' });
      handleResetCrForm();
      fetchCrPromptsForPaper(selectedPaperId);
    } catch (err: any) {
      setCrStatusMsg({ type: 'error', text: err.message || 'Failed to save constructed response prompt.' });
    } finally {
      setCrSubmitting(false);
    }
  };

  const handleDeleteCrPrompt = async (promptId: string) => {
    if (!window.confirm('Delete this constructed response prompt permanently from Firestore?')) return;
    try {
      await deleteConstructedResponseFromFirestore(selectedPaperId, promptId);
      fetchCrPromptsForPaper(selectedPaperId);
    } catch (err: any) {
      alert(`Error deleting constructed response prompt: ${err.message}`);
    }
  };

  return (
    <div className="space-y-6 pb-12">
      {/* Top Banner with Clear ADMIN Badge */}
      <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-white shadow-xl relative overflow-hidden">
        <div className="absolute -right-6 -bottom-6 w-40 h-40 bg-indigo-500/10 rounded-full blur-2xl pointer-events-none" />

        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 relative z-10">
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="px-3 py-1 bg-amber-500 text-slate-950 font-black text-xs uppercase tracking-wider rounded-md flex items-center gap-1.5 shadow-md">
                <ShieldCheck className="w-4 h-4 text-slate-950" /> ADMIN VIEW
              </span>
              <span className="px-2.5 py-1 bg-indigo-950 text-indigo-300 border border-indigo-800/80 font-mono text-[11px] rounded-md font-bold">
                UID: {ADMIN_UID}
              </span>
            </div>

            <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mt-1">
              PRAXIS Prep Pro Content Manager
            </h1>
            <p className="text-slate-300 text-xs sm:text-sm max-w-2xl">
              Manage standardized papers, question pools, and study guides. All content saved here writes directly to Firestore and powers the live exam simulator and student guides.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={fetchDashboardData}
              disabled={loading}
              className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs px-4 py-2.5 rounded-xl transition flex items-center gap-2 shadow-xs cursor-pointer disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              <span>Refresh Firestore</span>
            </button>
          </div>
        </div>

        {/* Quick Stat Bar */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-6 border-t border-slate-800/80 text-xs">
          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/60">
            <div className="text-slate-400 font-semibold flex items-center gap-1.5 mb-1">
              <Users className="w-4 h-4 text-indigo-400" /> Total Users
            </div>
            <div className="text-2xl font-black text-white">{(users || []).length}</div>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/60">
            <div className="text-slate-400 font-semibold flex items-center gap-1.5 mb-1">
              <HelpCircle className="w-4 h-4 text-emerald-400" /> Total Questions
            </div>
            <div className="text-2xl font-black text-white">{totalQuestionsCount || 0}</div>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/60">
            <div className="text-slate-400 font-semibold flex items-center gap-1.5 mb-1">
              <Crown className="w-4 h-4 text-amber-400" /> Paid Tiers
            </div>
            <div className="text-2xl font-black text-white">
              {(users || []).filter(u => u?.tier && u.tier !== 'free').length}
            </div>
          </div>

          <div className="bg-slate-800/60 p-3.5 rounded-xl border border-slate-700/60">
            <div className="text-slate-400 font-semibold flex items-center gap-1.5 mb-1">
              <HardDrive className="w-4 h-4 text-indigo-300" /> Security Status
            </div>
            <div className="text-sm font-bold text-emerald-400 flex items-center gap-1 mt-1">
              <CheckCircle2 className="w-4 h-4" /> Rules Enforced
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Manager Section */}
      <div className="bg-white rounded-2xl border border-slate-200 shadow-xs p-6 space-y-6">
        {/* Navigation Sub-Tabs */}
        <div className="flex items-center gap-3 border-b border-slate-200 pb-3">
          <button
            onClick={() => setContentTab('papers')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'papers'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <BookOpen className="w-4 h-4" /> PRAXIS Papers ({(papers || []).length})
          </button>

          <button
            onClick={() => setContentTab('questions')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'questions'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <HelpCircle className="w-4 h-4" /> Question Pool ({(existingQuestions || []).length})
          </button>

          <button
            onClick={() => setContentTab('study')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'study'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileText className="w-4 h-4" /> Study Materials ({(existingGuides || []).length})
          </button>

          <button
            onClick={() => setContentTab('flashcards')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'flashcards'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <CreditCard className="w-4 h-4" /> Flashcard Deck ({(existingCards || []).length})
          </button>

          <button
            onClick={() => setContentTab('essays')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'essays'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <PenTool className="w-4 h-4" /> Essay Prompts ({(existingPrompts || []).length})
          </button>

          <button
            onClick={() => setContentTab('constructedResponses')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'constructedResponses'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <FileSignature className="w-4 h-4" /> Constructed Responses ({(existingCrPrompts || []).length})
          </button>

          <button
            onClick={() => setContentTab('blog')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'blog'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Newspaper className="w-4 h-4" /> Blog ({(existingBlogPosts || []).length})
          </button>

          <button
            onClick={() => setContentTab('users')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'users'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <Users className="w-4 h-4" /> Registered Users ({(users || []).length})
          </button>

          <button
            onClick={() => setContentTab('tutoringBookings')}
            className={`px-4 py-2 rounded-xl font-bold text-xs flex items-center gap-2 transition cursor-pointer ${
              contentTab === 'tutoringBookings'
                ? 'bg-indigo-600 text-white shadow-xs'
                : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
            }`}
          >
            <GraduationCap className="w-4 h-4" /> Tutoring Bookings ({(tutoringBookings || []).length})
          </button>
        </div>

        {/* TAB 1: PAPERS MANAGER */}
        {contentTab === 'papers' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create/Edit Paper Form (Left) */}
            <div 
              ref={paperFormRef} 
              className={`lg:col-span-1 p-5 rounded-2xl border transition-all space-y-4 ${
                editingPaperId 
                  ? 'bg-indigo-50/40 border-indigo-300 ring-2 ring-indigo-500/20 shadow-xs' 
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <Edit3 className="w-4 h-4 text-indigo-600" />
                  {editingPaperId ? `Edit Paper (${editingPaperId})` : 'Add New PRAXIS Paper'}
                </h3>
                {editingPaperId && (
                  <button
                    onClick={handleResetPaperForm}
                    className="text-xs text-indigo-600 hover:underline font-bold"
                  >
                    + New Paper
                  </button>
                )}
              </div>

              {paperStatusMsg && (
                <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  paperStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {paperStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{paperStatusMsg.text}</span>
                </div>
              )}

              <form onSubmit={handleSavePaper} className="space-y-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paper Code (e.g., 5713)</label>
                  <input
                    ref={paperCodeInputRef}
                    type="text"
                    required
                    value={paperCode}
                    onChange={(e) => setPaperCode(e.target.value)}
                    placeholder="e.g. 5713"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Paper Title</label>
                  <input
                    type="text"
                    required
                    value={paperTitle}
                    onChange={(e) => setPaperTitle(e.target.value)}
                    placeholder="e.g. Core Academic Skills for Educators: Reading"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Exam Code / Family</label>
                    <select
                      value={paperExamCode}
                      onChange={(e) => setPaperExamCode(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-2.5 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    >
                      <optgroup label="Praxis Series">
                        <option value="Praxis-Core">Praxis-Core</option>
                        <option value="Praxis-Subject">Praxis-Subject</option>
                        <option value="Praxis-PLT">Praxis-PLT</option>
                      </optgroup>
                      <optgroup label="California">
                        <option value="CBEST">CBEST</option>
                        <option value="CSET">CSET</option>
                        <option value="FoRT">FoRT</option>
                        <option value="RICA">RICA (Retired)</option>
                        <option value="edTPA">edTPA</option>
                      </optgroup>
                      <optgroup label="New York">
                        <option value="NYSTCE-EAS">NYSTCE-EAS</option>
                        <option value="NYSTCE-CST">NYSTCE-CST</option>
                      </optgroup>
                      <optgroup label="State Certifications">
                        <option value="TExES">TExES (Texas)</option>
                        <option value="FTCE">FTCE (Florida)</option>
                        <option value="ILTS">ILTS (Illinois)</option>
                        <option value="PECT">PECT (Pennsylvania)</option>
                        <option value="GACE">GACE (Georgia)</option>
                      </optgroup>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Category</label>
                    <select
                      value={paperCategory}
                      onChange={(e) => setPaperCategory(e.target.value as any)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-2.5 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="core">Core Academic</option>
                      <option value="elementary">Elementary Ed</option>
                      <option value="plt">PLT K-12</option>
                      <option value="subject">Subject Spec.</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-2">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Format</label>
                    <select
                      value={paperContentType}
                      onChange={(e) => setPaperContentType(e.target.value as any)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-2.5 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="standard">Multiple Choice</option>
                      <option value="essay">Constructed Essay</option>
                      <option value="mixed">Mixed (MC + Constructed)</option>
                      <option value="performance_assessment">Performance Assessment (Portfolio)</option>
                    </select>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Duration (Mins)</label>
                    <input
                      type="number"
                      value={paperDuration}
                      onChange={(e) => setPaperDuration(e.target.value)}
                      placeholder="e.g. 90"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">ETS Passing Mark</label>
                    <input
                      type="number"
                      value={paperPassingScore}
                      onChange={(e) => setPaperPassingScore(e.target.value)}
                      placeholder="e.g. 156"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Description</label>
                  <textarea
                    rows={2}
                    value={paperDescription}
                    onChange={(e) => setPaperDescription(e.target.value)}
                    placeholder="Summary of what this PRAXIS paper tests..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Domains & Weightages (Comma Separated)</label>
                  <input
                    type="text"
                    value={paperDomainsText}
                    onChange={(e) => setPaperDomainsText(e.target.value)}
                    placeholder="Domain 1 (30%), Domain 2 (70%)"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={paperSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>{editingPaperId ? 'Update Paper in Firestore' : 'Save Paper to Firestore'}</span>
                </button>
              </form>
            </div>

            {/* Existing Papers List (Right 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                  <Layers className="w-5 h-5 text-indigo-600" /> Active Standardized Papers ({(papers || []).length})
                </h3>
              </div>

              {loading ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-slate-100 h-20 rounded-xl" />
                  ))}
                </div>
              ) : (papers || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <BookOpen className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No papers yet — add your first one above</div>
                  <p className="max-w-xs mx-auto">Fill out the form on the left to create a PRAXIS paper specification in Firestore.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {papers.map(p => {
                    const isSelectedForEdit = editingPaperId === p.id;
                    const isEssayPaper = p.contentType === 'essay';
                    const isMixedPaper = p.contentType === 'mixed';
                    const isPerformanceAssessment = p.contentType === 'performance_assessment';

                    return (
                      <div
                        key={p.id}
                        className={`border p-4 rounded-xl transition space-y-3 flex flex-col justify-between ${
                          isSelectedForEdit
                            ? 'bg-indigo-50/50 border-indigo-400 ring-2 ring-indigo-500/20'
                            : 'bg-slate-50 border-slate-200 hover:border-indigo-300'
                        }`}
                      >
                        <div className="space-y-1">
                          <div className="flex items-center justify-between gap-1 flex-wrap">
                            <div className="flex items-center gap-1.5">
                              <span className="px-2 py-0.5 bg-indigo-100 text-indigo-900 font-black text-[10px] rounded uppercase">
                                {p.code}
                              </span>
                              {p.examCode && (
                                <span className="px-1.5 py-0.5 bg-purple-100 text-purple-800 font-bold text-[9px] rounded">
                                  {p.examCode}
                                </span>
                              )}
                              {isEssayPaper && (
                                <span className="px-1.5 py-0.5 bg-amber-100 text-amber-900 font-bold text-[9px] rounded">
                                  Essay
                                </span>
                              )}
                              {isMixedPaper && (
                                <span className="px-1.5 py-0.5 bg-emerald-100 text-emerald-900 font-bold text-[9px] rounded">
                                  MC + CR
                                </span>
                              )}
                              {isPerformanceAssessment && (
                                <span className="px-1.5 py-0.5 bg-sky-100 text-sky-900 font-bold text-[9px] rounded">
                                  Portfolio
                                </span>
                              )}
                              {isSelectedForEdit && (
                                <span className="px-1.5 py-0.5 bg-indigo-600 text-white font-extrabold text-[9px] rounded uppercase tracking-wider animate-pulse">
                                  Editing Now
                                </span>
                              )}
                            </div>
                            <span className="text-[11px] font-bold text-slate-500">
                              {p.isPassingScoreProvisional || p.passingScoreNote ? (
                                <span className="text-amber-800 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200" title={p.passingScoreNote || 'Provisional passing mark'}>
                                  Passing: {p.passingScoreEts ?? 240} (Provisional)
                                </span>
                              ) : p.passingScoreEts === null || p.passingScoreEts === undefined ? (
                                <span className="text-slate-400 italic">Passing score: Not yet confirmed</span>
                              ) : isEssayPaper ? (
                                <>Passing: {p.passingScoreEts}{p.scoreScaleMax ? ` / ${p.scoreScaleMax}` : ''}</>
                              ) : p.examCode && !p.examCode.toLowerCase().startsWith('praxis') ? (
                                p.scoreScaleMin !== null && p.scoreScaleMin !== undefined && p.scoreScaleMax !== null && p.scoreScaleMax !== undefined ? (
                                  <>Passing: {p.passingScoreEts} (Scale {p.scoreScaleMin}-{p.scoreScaleMax})</>
                                ) : (
                                  <>Passing: {p.passingScoreEts}</>
                                )
                              ) : (
                                <>Passing: {p.passingScoreEts} ETS</>
                              )}
                            </span>
                          </div>

                          <div className="font-extrabold text-slate-900 text-sm line-clamp-1">{p.title}</div>
                          <p className="text-slate-500 text-xs line-clamp-2">{p.description}</p>
                        </div>

                        <div className="pt-2 border-t border-slate-200/80 flex flex-col gap-2">
                          <div className="flex items-center justify-between text-xs">
                            <span className="text-slate-600 font-semibold">
                              {isEssayPaper ? (
                                <>Prompts: <strong>{p.essayPrompts?.length || 0}</strong></>
                              ) : isMixedPaper ? (
                                <>MC: <strong>{p.questions?.length || p.totalQuestions || 0}</strong> | CR: <strong>{p.constructedResponsePrompts?.length || 0}</strong></>
                              ) : isPerformanceAssessment ? (
                                <>Tasks: <strong>{p.performanceTasks?.length || 0}</strong> (Portfolio)</>
                              ) : (
                                <>Questions: <strong>{p.questions?.length || p.totalQuestions || 0}</strong></>
                              )}
                            </span>
                            <div className="flex items-center gap-1.5">
                              <button
                                onClick={() => handleSelectPaperForEdit(p)}
                                className={`px-3 py-1 font-bold border rounded-lg transition cursor-pointer text-xs ${
                                  isSelectedForEdit
                                    ? 'bg-indigo-600 text-white border-indigo-600 shadow-xs'
                                    : 'bg-white hover:bg-slate-100 text-indigo-600 border-slate-300'
                                }`}
                              >
                                {isSelectedForEdit ? 'Editing' : 'Edit'}
                              </button>
                              <button
                                onClick={() => handleDeletePaper(p.id, p.title)}
                                className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition cursor-pointer"
                                title="Delete paper"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>

                          {/* Quick Management Links to Sub-Tabs */}
                          <div className="flex items-center gap-1.5 pt-1 border-t border-slate-200/60">
                            {isEssayPaper ? (
                              <button
                                onClick={() => {
                                  setSelectedPaperId(p.id);
                                  setContentTab('essays');
                                }}
                                className="flex-1 px-2.5 py-1.5 bg-amber-50 hover:bg-amber-100 text-amber-900 font-bold text-[11px] border border-amber-200 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <PenTool className="w-3.5 h-3.5 text-amber-700" />
                                <span>Manage Essays</span>
                              </button>
                            ) : isMixedPaper ? (
                              <>
                                <button
                                  onClick={() => {
                                    setSelectedPaperId(p.id);
                                    setContentTab('questions');
                                  }}
                                  className="flex-1 px-2.5 py-1.5 bg-white hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 font-bold text-[11px] border border-slate-200 hover:border-indigo-300 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                                >
                                  <HelpCircle className="w-3.5 h-3.5 text-indigo-600" />
                                  <span>Manage Questions</span>
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedPaperId(p.id);
                                    setContentTab('constructedResponses');
                                  }}
                                  className="flex-1 px-2.5 py-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-900 font-bold text-[11px] border border-indigo-200 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                                >
                                  <FileSignature className="w-3.5 h-3.5 text-indigo-700" />
                                  <span>Manage Constructed Responses</span>
                                </button>
                              </>
                            ) : isPerformanceAssessment ? (
                              <button
                                onClick={() => {
                                  setSelectedPaperId(p.id);
                                  setContentTab('performanceTasks');
                                }}
                                className="flex-1 px-2.5 py-1.5 bg-sky-50 hover:bg-sky-100 text-sky-900 font-bold text-[11px] border border-sky-200 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <Layers className="w-3.5 h-3.5 text-sky-700" />
                                <span>Manage Performance Tasks</span>
                              </button>
                            ) : (
                              <button
                                onClick={() => {
                                  setSelectedPaperId(p.id);
                                  setContentTab('questions');
                                }}
                                className="flex-1 px-2.5 py-1.5 bg-white hover:bg-indigo-50 text-slate-700 hover:text-indigo-700 font-bold text-[11px] border border-slate-200 hover:border-indigo-300 rounded-lg transition flex items-center justify-center gap-1.5 cursor-pointer shadow-2xs"
                              >
                                <HelpCircle className="w-3.5 h-3.5 text-indigo-600" />
                                <span>Manage Questions</span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: QUESTIONS POOL MANAGER */}
        {contentTab === 'questions' && (
          <div className="space-y-6">
            {/* Top Toolbar: Paper Selector & Sub-Mode Toggle */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3">
                <label className="text-xs font-extrabold text-slate-800 whitespace-nowrap">Target Paper:</label>
                <select
                  value={selectedPaperId}
                  onChange={(e) => setSelectedPaperId(e.target.value)}
                  className="bg-white border border-slate-300 font-bold text-xs text-slate-900 rounded-xl px-3 py-2 focus:ring-2 focus:ring-indigo-500 shadow-xs"
                >
                  {papers.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code} - {p.title}
                    </option>
                  ))}
                </select>
              </div>

              <div className="flex items-center gap-2">
                <button
                  onClick={() => setQuestionSubMode('manual')}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                    questionSubMode === 'manual'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  + Manual Question Form
                </button>
                <button
                  onClick={() => setQuestionSubMode('bulk')}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer ${
                    questionSubMode === 'bulk'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  <Upload className="w-3.5 h-3.5 inline mr-1" /> Bulk Import (CSV/JSON)
                </button>
                <button
                  onClick={() => setQuestionSubMode('ai')}
                  className={`px-3 py-1.5 rounded-xl font-bold text-xs transition cursor-pointer flex items-center gap-1.5 ${
                    questionSubMode === 'ai'
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white text-slate-700 border border-slate-300'
                  }`}
                >
                  <Sparkles className="w-3.5 h-3.5" />
                  <span>Generate with AI</span>
                </button>
              </div>
            </div>

            {/* MANUAL QUESTION FORM */}
            {questionSubMode === 'manual' && (
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <Plus className="w-4 h-4 text-indigo-600" />
                  Add Question to PRAXIS Paper #{selectedPaperId}
                </h3>

                {questionStatusMsg && (
                  <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                    questionStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                  }`}>
                    {questionStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                    <span>{questionStatusMsg.text}</span>
                  </div>
                )}

                <form onSubmit={handleSaveManualQuestion} className="space-y-4 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        Domain / Subtest (Constrained to Parent Paper)
                      </label>
                      <select
                        value={qDomain}
                        onChange={(e) => setQDomain(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500 shadow-2xs"
                      >
                        {activePaperDomains.map((domName, dIdx) => (
                          <option key={dIdx} value={domName}>
                            {domName}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Difficulty Level</label>
                      <select
                        value={qDifficulty}
                        onChange={(e) => setQDifficulty(e.target.value as any)}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="easy">Easy</option>
                        <option value="medium">Medium</option>
                        <option value="hard">Hard</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Question Prompt Text</label>
                    <textarea
                      rows={3}
                      required
                      value={qText}
                      onChange={(e) => setQText(e.target.value)}
                      placeholder="Enter the complete question prompt..."
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  {/* 4/5 Options Inputs */}
                  <div className="space-y-2">
                    <label className="block font-bold text-slate-700">Answer Options (Select correct radio button)</label>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {[
                        { label: 'Option A', val: qOption0, setVal: setQOption0, idx: 0 },
                        { label: 'Option B', val: qOption1, setVal: setQOption1, idx: 1 },
                        { label: 'Option C', val: qOption2, setVal: setQOption2, idx: 2 },
                        { label: 'Option D', val: qOption3, setVal: setQOption3, idx: 3 },
                        { label: 'Option E (Optional for 5-choice exams)', val: qOption4, setVal: setQOption4, idx: 4 }
                      ].map((opt) => (
                        <div key={opt.idx} className="flex items-center gap-2 bg-white p-2 border border-slate-300 rounded-xl">
                          <input
                            type="radio"
                            name="correctOpt"
                            checked={qCorrectIndex === opt.idx}
                            onChange={() => setQCorrectIndex(opt.idx)}
                            className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                          />
                          <input
                            type="text"
                            required={opt.idx < 2}
                            value={opt.val}
                            onChange={(e) => opt.setVal(e.target.value)}
                            placeholder={`${opt.label}...`}
                            className="w-full text-slate-900 font-medium focus:outline-none text-xs"
                          />
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Detailed Explanation & Rationale</label>
                    <textarea
                      rows={2}
                      value={qExplanation}
                      onChange={(e) => setQExplanation(e.target.value)}
                      placeholder="Explain why the correct answer is right and why distractors are incorrect..."
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={questionSubmitting}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 px-6 rounded-xl transition shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-2"
                  >
                    <Plus className="w-4 h-4" /> Save Question to Firestore
                  </button>
                </form>
              </div>
            )}

            {/* BULK IMPORT SECTION */}
            {questionSubMode === 'bulk' && (
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <Upload className="w-4 h-4 text-indigo-600" />
                  Bulk Import Questions (CSV / JSON) for Paper #{selectedPaperId}
                </h3>

                <p className="text-xs text-slate-600">
                  Upload a CSV or JSON file containing question records. The client parser validates schema before batch-writing to Firestore.
                </p>

                {bulkError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{bulkError}</span>
                  </div>
                )}

                <div className="flex items-center gap-4">
                  <label className="bg-white hover:bg-slate-100 border border-slate-300 px-4 py-2 rounded-xl text-xs font-bold text-slate-800 transition cursor-pointer flex items-center gap-2">
                    <Upload className="w-4 h-4 text-indigo-600" />
                    <span>Choose CSV or JSON File</span>
                    <input
                      type="file"
                      accept=".csv,.json"
                      onChange={handleFileUploadForBulk}
                      className="hidden"
                    />
                  </label>
                  {bulkFileName && (
                    <span className="text-xs font-mono font-bold text-indigo-600">{bulkFileName}</span>
                  )}
                </div>
              </div>
            )}

            {/* AI QUESTION GENERATOR SECTION */}
            {questionSubMode === 'ai' && (
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div>
                    <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-indigo-600" />
                      AI Practice Question Generator for Paper #{selectedPaperId}
                    </h3>
                    <p className="text-xs text-slate-600">
                      Generate 100% original, standards-aligned multiple-choice questions with full pedagogical explanations using Gemini.
                    </p>
                  </div>
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl bg-indigo-50 border border-indigo-200 text-indigo-800 text-[11px] font-bold self-start sm:self-auto">
                    <BrainCircuit className="w-3.5 h-3.5 text-indigo-600" />
                    Gemini 3.6 Flash Structured Output
                  </span>
                </div>

                {aiGenError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-800 rounded-xl text-xs font-bold flex items-center gap-2">
                    <AlertCircle className="w-4 h-4 shrink-0" />
                    <span>{aiGenError}</span>
                  </div>
                )}

                <form onSubmit={handleGenerateQuestionsWithAI} className="space-y-4 text-xs">
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    {/* Domain Selection */}
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">
                        Target Domain / Subtest (Constrained)
                      </label>
                      <select
                        value={aiGenDomain}
                        onChange={(e) => setAiGenDomain(e.target.value)}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500 shadow-2xs"
                      >
                        {activePaperDomains.map((domName, dIdx) => (
                          <option key={dIdx} value={domName}>
                            {domName}
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Difficulty Selection */}
                    <div>
                      <label className="block font-bold text-slate-700 mb-1">Target Difficulty</label>
                      <select
                        value={aiGenDifficulty}
                        onChange={(e) => setAiGenDifficulty(e.target.value as any)}
                        className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 shadow-2xs"
                      >
                        <option value="easy">Easy (Foundational Recall & Terms)</option>
                        <option value="medium">Medium (Standard Teacher Exam Depth)</option>
                        <option value="hard">Hard (Complex Scenarios & Pedagogy Analysis)</option>
                      </select>
                    </div>

                    {/* Question Count Slider */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="font-bold text-slate-700">Questions to Generate</label>
                        <span className="font-mono font-bold text-indigo-600">{aiGenCount} items (Max 20)</span>
                      </div>
                      <input
                        type="range"
                        min={1}
                        max={20}
                        value={aiGenCount}
                        onChange={(e) => setAiGenCount(parseInt(e.target.value, 10))}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 font-mono mt-0.5">
                        <span>1</span>
                        <span>5</span>
                        <span>10</span>
                        <span>15</span>
                        <span>20 max</span>
                      </div>
                    </div>
                  </div>

                  <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-[11px] text-amber-900 flex items-start gap-2">
                    <Wand2 className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
                    <div>
                      <strong>Safe Staging Preview:</strong> Generated questions appear in the preview table below without writing anything to Firestore. You can review or discard items before confirming the batch-write.
                    </div>
                  </div>

                  <button
                    type="submit"
                    disabled={aiGenerating}
                    className="bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold py-2.5 px-6 rounded-xl transition shadow-xs cursor-pointer disabled:opacity-50 flex items-center gap-2"
                  >
                    {aiGenerating ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Generating {aiGenCount} Questions with Gemini...</span>
                      </>
                    ) : (
                      <>
                        <Sparkles className="w-4 h-4" />
                        <span>Generate {aiGenCount} Practice Questions</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            )}

            {/* PREVIEW TABLE FOR BULK IMPORT / AI GENERATION */}
            {bulkParsedQuestions.length > 0 && (
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-3">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div>
                    <span className="font-extrabold text-slate-900 text-xs">
                      Staged Questions Preview ({bulkParsedQuestions.length} items ready for Paper #{selectedPaperId})
                    </span>
                    {bulkFileName && (
                      <p className="text-[11px] font-mono text-indigo-600">{bulkFileName}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => {
                        setBulkParsedQuestions([]);
                        setBulkFileName(null);
                      }}
                      className="px-3 py-2 bg-white hover:bg-slate-100 text-slate-700 font-bold rounded-xl text-xs border border-slate-300 transition cursor-pointer"
                    >
                      Clear Staged
                    </button>
                    <button
                      onClick={handleConfirmBulkWrite}
                      disabled={bulkSubmitting}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white font-black px-4 py-2 rounded-xl text-xs shadow-xs transition flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      <FileCheck className="w-4 h-4" />
                      <span>Confirm & Batch-Write {bulkParsedQuestions.length} Questions</span>
                    </button>
                  </div>
                </div>

                <div className="max-h-80 overflow-y-auto border border-slate-200 rounded-xl bg-white">
                  <table className="w-full text-left border-collapse text-xs">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200 font-bold text-slate-600">
                        <th className="p-2.5">#</th>
                        <th className="p-2.5">Domain</th>
                        <th className="p-2.5">Question Text</th>
                        <th className="p-2.5">Difficulty</th>
                        <th className="p-2.5">Options</th>
                        <th className="p-2.5">Correct Ans</th>
                        <th className="p-2.5 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {bulkParsedQuestions.map((q, idx) => (
                        <tr key={idx} className="hover:bg-slate-50">
                          <td className="p-2.5 font-mono text-slate-400">{idx + 1}</td>
                          <td className="p-2.5 font-semibold text-slate-700">{q.domain}</td>
                          <td className="p-2.5 max-w-sm truncate text-slate-900" title={q.questionText}>
                            {q.questionText}
                          </td>
                          <td className="p-2.5">
                            <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase rounded ${
                              q.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-800' :
                              q.difficulty === 'hard' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                            }`}>
                              {q.difficulty}
                            </span>
                          </td>
                          <td className="p-2.5 font-bold text-indigo-600">
                            {q.options?.length || 0} opts
                          </td>
                          <td className="p-2.5 font-bold text-emerald-700">
                            {q.options?.[q.correctAnswerIndex] ? (
                              <span title={q.options[q.correctAnswerIndex]}>
                                {String.fromCharCode(65 + q.correctAnswerIndex)} ({q.options[q.correctAnswerIndex].slice(0, 18)}...)
                              </span>
                            ) : (
                              String.fromCharCode(65 + (q.correctAnswerIndex || 0))
                            )}
                          </td>
                          <td className="p-2.5 text-right">
                            <button
                              type="button"
                              onClick={() => setBulkParsedQuestions(prev => prev.filter((_, i) => i !== idx))}
                              className="p-1 text-slate-400 hover:text-red-600 transition cursor-pointer"
                              title="Remove this question from staging"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Existing Questions Pool List for Selected Paper */}
            <div className="space-y-3">
              <h3 className="font-extrabold text-slate-900 text-sm">
                Questions Currently Loaded for Paper #{selectedPaperId} ({(existingQuestions || []).length})
              </h3>

              {loadingQuestions ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-slate-100 h-16 rounded-xl" />
                  ))}
                </div>
              ) : (existingQuestions || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <HelpCircle className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No questions yet — add your first one above</div>
                  <p className="max-w-xs mx-auto">Use the manual form or bulk import CSV/JSON to populate question items for this paper.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {existingQuestions.map((q, idx) => (
                    <div key={q.id || idx} className="p-4 bg-white border border-slate-200 rounded-xl space-y-2 text-xs">
                      <div className="flex items-center justify-between">
                        <span className="px-2 py-0.5 bg-indigo-50 text-indigo-800 font-bold text-[10px] rounded">
                          Q{idx + 1} • {q.domain}
                        </span>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 text-[10px] font-extrabold uppercase rounded ${
                            q.difficulty === 'easy' ? 'bg-emerald-100 text-emerald-800' :
                            q.difficulty === 'hard' ? 'bg-red-100 text-red-800' : 'bg-amber-100 text-amber-800'
                          }`}>
                            {q.difficulty}
                          </span>
                          <button
                            onClick={() => handleEditQuestionClick(q)}
                            className="p-1 text-slate-500 hover:text-indigo-600 transition cursor-pointer"
                            title="Edit Question"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteQuestion(q.id)}
                            className="p-1 text-slate-500 hover:text-red-600 transition cursor-pointer"
                            title="Delete Question"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="font-bold text-slate-900 text-sm">{q.questionText}</div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-1">
                        {q.options?.map((opt, oIdx) => (
                          <div
                            key={oIdx}
                            className={`p-2 rounded-lg border text-xs font-medium ${
                              oIdx === q.correctAnswerIndex
                                ? 'bg-emerald-50 border-emerald-300 text-emerald-900 font-bold'
                                : 'bg-slate-50 border-slate-200 text-slate-700'
                            }`}
                          >
                            <span className="font-mono mr-1.5">{String.fromCharCode(65 + oIdx)}.</span>
                            {opt}
                          </div>
                        ))}
                      </div>

                      {q.explanation && (
                        <div className="p-2.5 bg-slate-50 text-slate-600 rounded-lg text-[11px]">
                          <strong>Explanation:</strong> {q.explanation}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 3: STUDY MATERIALS MANAGER */}
        {contentTab === 'study' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create Study Guide Form */}
            <div className="lg:col-span-1 bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                <FileText className="w-4 h-4 text-indigo-600" />
                Add Study Guide & Printable PDF
              </h3>

              {guideStatusMsg && (
                <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  guideStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {guideStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{guideStatusMsg.text}</span>
                </div>
              )}

              <form onSubmit={handleSaveStudyGuide} className="space-y-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Target Paper</label>
                  <select
                    value={selectedPaperId}
                    onChange={(e) => setSelectedPaperId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  >
                    {papers.map(p => (
                      <option key={p.id} value={p.id}>
                        PRAXIS {p.code} - {p.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Study Guide Title</label>
                  <input
                    type="text"
                    required
                    value={guideTitle}
                    onChange={(e) => setGuideTitle(e.target.value)}
                    placeholder="e.g. High-Yield Domain 1 Cheat Sheet"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Short Description (shown on the student-facing guide tile)</label>
                  <input
                    type="text"
                    value={guideSummary}
                    onChange={(e) => setGuideSummary(e.target.value)}
                    placeholder="e.g. Content categories, item weighting, and high-yield formulas for the Mathematics CK exam."
                    maxLength={160}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Markdown / Study Text Content</label>
                  <textarea
                    rows={5}
                    required
                    value={guideMarkdown}
                    onChange={(e) => setGuideMarkdown(e.target.value)}
                    placeholder="Enter study guide markdown, key formulas, or exam tips..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Upload Companion PDF (Firebase Storage)</label>
                  <input
                    type="file"
                    accept=".pdf,.doc,.docx"
                    onChange={(e) => setGuideFile(e.target.files?.[0] || null)}
                    className="w-full text-xs text-slate-600"
                  />
                  {guideFile && (
                    <div className="text-[11px] text-indigo-600 font-bold mt-1">
                      Ready to upload: {guideFile.name}
                    </div>
                  )}
                </div>

                <button
                  type="submit"
                  disabled={guideSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>Save Study Guide to Firestore</span>
                </button>
              </form>
            </div>

            {/* Study Guides List (Right 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                <FileText className="w-5 h-5 text-indigo-600" />
                Study Guides for Paper #{selectedPaperId} ({(existingGuides || []).length})
              </h3>

              {loadingGuides ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2].map(i => (
                    <div key={i} className="bg-slate-100 h-24 rounded-xl" />
                  ))}
                </div>
              ) : (existingGuides || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <FileText className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No study materials yet — upload your first guide above</div>
                  <p className="max-w-xs mx-auto">Add markdown summaries and attach downloadable PDFs to support student review.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {existingGuides.map(guide => (
                    <div key={guide.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="font-extrabold text-slate-900 text-sm">{guide.title}</div>
                          {guide.summary && (
                            <div className="text-[11px] text-slate-600 mt-0.5">{guide.summary}</div>
                          )}
                          <div className="text-[11px] text-slate-500 mt-0.5">
                            Updated: {new Date(guide.updatedAt).toLocaleDateString()}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          {(guide.pdfUrl || guide.pdfStoragePath) && (
                            <button
                              type="button"
                              onClick={() => handlePreviewGuidePdf(guide.id)}
                              disabled={guidePdfPreviewLoadingId === guide.id}
                              className="px-2.5 py-1 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-bold text-xs rounded-lg transition flex items-center gap-1"
                            >
                              {guidePdfPreviewLoadingId === guide.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <ExternalLink className="w-3.5 h-3.5" />
                              )}
                              PDF
                            </button>
                          )}
                          <button
                            onClick={() => handleDeleteGuide(guide.id)}
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition cursor-pointer"
                            title="Delete Study Guide"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="text-xs text-slate-700 bg-white p-3 rounded-lg border border-slate-200 line-clamp-3">
                        {guide.contentMarkdown}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB: BLOG POST MANAGER */}
        {contentTab === 'blog' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create/Edit Blog Post Form (Left) */}
            <div
              className={`lg:col-span-1 p-5 rounded-2xl border transition-all space-y-4 ${
                editingBlogId
                  ? 'bg-indigo-50/40 border-indigo-300 ring-2 ring-indigo-500/20 shadow-xs'
                  : 'bg-slate-50 border-slate-200'
              }`}
            >
              <div className="flex items-center justify-between gap-2">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <Newspaper className="w-4 h-4 text-indigo-600" />
                  {editingBlogId ? 'Edit Blog Post' : 'Write a New Blog Post'}
                </h3>
                {editingBlogId && (
                  <button
                    onClick={handleResetBlogForm}
                    className="text-[11px] font-bold text-slate-500 hover:text-slate-700 cursor-pointer"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              {blogStatusMsg && (
                <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  blogStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {blogStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{blogStatusMsg.text}</span>
                </div>
              )}

              <form onSubmit={(e) => handleSaveBlogPost(e)} className="space-y-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Title</label>
                  <input
                    type="text"
                    required
                    value={blogTitle}
                    onChange={(e) => {
                      setBlogTitle(e.target.value);
                      if (!editingBlogId) setBlogSlug(slugify(e.target.value));
                    }}
                    placeholder="e.g. 5 High-Yield Strategies for CBEST Math"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">URL Slug</label>
                  <input
                    type="text"
                    value={blogSlug}
                    onChange={(e) => setBlogSlug(slugify(e.target.value))}
                    placeholder="auto-generated-from-title"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Cover Image URL</label>
                  <input
                    type="text"
                    value={blogImageUrl}
                    onChange={(e) => setBlogImageUrl(e.target.value)}
                    placeholder="https://images.unsplash.com/... (paste a direct image link)"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                  <p className="text-[10px] text-slate-400 mt-1">Shown as the banner on the blog card and article page. Leave blank for no image.</p>
                  {blogImageUrl.trim() && (
                    <img
                      src={blogImageUrl.trim()}
                      alt="Cover preview"
                      className="mt-2 w-full h-28 object-cover rounded-lg border border-slate-200"
                      onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                      onLoad={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'block'; }}
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Exam Family</label>
                    <select
                      value={blogExamFamily}
                      onChange={(e) => setBlogExamFamily(e.target.value)}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    >
                      {BLOG_EXAM_FAMILIES.map(fam => (
                        <option key={fam} value={fam}>{fam}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">State / Region</label>
                    <input
                      type="text"
                      value={blogState}
                      onChange={(e) => setBlogState(e.target.value)}
                      placeholder="e.g. California"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Summary (shown on the card)</label>
                  <textarea
                    rows={2}
                    required
                    value={blogSummary}
                    onChange={(e) => setBlogSummary(e.target.value)}
                    placeholder="One or two sentences describing the article..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Full Article (Markdown)</label>
                  <textarea
                    rows={8}
                    value={blogContentMarkdown}
                    onChange={(e) => setBlogContentMarkdown(e.target.value)}
                    placeholder="Write the full article here..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Read Time</label>
                    <input
                      type="text"
                      value={blogReadTime}
                      onChange={(e) => setBlogReadTime(e.target.value)}
                      placeholder="6 min read"
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  <div>
                    <label className="block font-bold text-slate-700 mb-1">Status</label>
                    <select
                      value={blogStatus}
                      onChange={(e) => setBlogStatus(e.target.value as 'draft' | 'published')}
                      className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    value={blogTagsText}
                    onChange={(e) => setBlogTagsText(e.target.value)}
                    placeholder="California, CBEST, Math Strategy"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div className="flex gap-2">
                  <button
                    type="submit"
                    disabled={blogSubmitting}
                    className="flex-1 bg-slate-700 hover:bg-slate-800 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Plus className="w-4 h-4" />
                    <span>{editingBlogId ? 'Save Changes' : 'Save as Draft'}</span>
                  </button>
                  <button
                    type="button"
                    disabled={blogSubmitting}
                    onClick={(e) => handleSaveBlogPost(e as any, true)}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    <span>Publish Now</span>
                  </button>
                </div>
              </form>
            </div>

            {/* Blog Posts List (Right 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                <Newspaper className="w-5 h-5 text-indigo-600" />
                All Blog Posts ({(existingBlogPosts || []).length})
              </h3>

              {loadingBlogPosts ? (
                <div className="space-y-3 animate-pulse">
                  {[1, 2].map(i => (
                    <div key={i} className="bg-slate-100 h-24 rounded-xl" />
                  ))}
                </div>
              ) : (existingBlogPosts || []).length === 0 ? (
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <Newspaper className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No blog posts yet — write your first one above</div>
                  <p className="max-w-xs mx-auto">Posts marked "Published" appear immediately on the public Blog page.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {existingBlogPosts.map(post => (
                    <div key={post.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex items-start gap-3">
                          {post.imageUrl ? (
                            <img
                              src={post.imageUrl}
                              alt=""
                              className="w-14 h-14 object-cover rounded-lg border border-slate-200 shrink-0"
                              onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = 'none'; }}
                            />
                          ) : null}
                          <div>
                          <div className="flex items-center gap-2">
                            <div className="font-extrabold text-slate-900 text-sm">{post.title}</div>
                            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold ${
                              post.status === 'published'
                                ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                                : 'bg-amber-50 text-amber-700 border border-amber-200'
                            }`}>
                              {post.status === 'published' ? 'Published' : 'Draft'}
                            </span>
                          </div>
                          <div className="text-[11px] text-slate-500">
                            {post.examFamily} &middot; Updated: {new Date(post.updatedAt).toLocaleDateString()}
                          </div>
                          </div>
                        </div>

                        <div className="flex items-center gap-2 shrink-0">
                          <button
                            onClick={() => handleToggleBlogPublish(post)}
                            className={`px-2.5 py-1 font-bold text-xs rounded-lg transition flex items-center gap-1 cursor-pointer ${
                              post.status === 'published'
                                ? 'bg-amber-50 hover:bg-amber-100 text-amber-700 border border-amber-200'
                                : 'bg-emerald-600 hover:bg-emerald-700 text-white'
                            }`}
                          >
                            {post.status === 'published' ? 'Unpublish' : 'Publish'}
                          </button>
                          <button
                            onClick={() => handleEditBlogClick(post)}
                            className="p-1.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-600 border border-indigo-200 rounded-lg transition cursor-pointer"
                            title="Edit Blog Post"
                          >
                            <Edit3 className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => handleDeleteBlogPost(post.id)}
                            className="p-1.5 bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 rounded-lg transition cursor-pointer"
                            title="Delete Blog Post"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      <div className="text-xs text-slate-700 bg-white p-3 rounded-lg border border-slate-200 line-clamp-2">
                        {post.summary}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 4: FLASHCARD DECK MANAGER */}
        {contentTab === 'flashcards' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create/Edit Flashcard Form */}
            <div className="lg:col-span-1 bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-indigo-600" />
                  {editingCardId ? 'Edit Flashcard' : 'Add Flashcard'}
                </h3>
                {editingCardId && (
                  <button
                    onClick={handleResetCardForm}
                    className="text-xs text-indigo-600 hover:underline font-bold"
                  >
                    + New Card
                  </button>
                )}
              </div>

              {cardStatusMsg && (
                <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  cardStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {cardStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  <span>{cardStatusMsg.text}</span>
                </div>
              )}

              <form onSubmit={handleSaveFlashcard} className="space-y-3 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Target Paper</label>
                  <select
                    value={selectedPaperId}
                    onChange={(e) => setSelectedPaperId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  >
                    {papers.map(p => (
                      <option key={p.id} value={p.id}>
                        PRAXIS {p.code} - {p.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Domain / Subject Category</label>
                  <input
                    type="text"
                    value={cardDomain}
                    onChange={(e) => setCardDomain(e.target.value)}
                    placeholder="e.g. Core Vocabulary, Pedagogy, Reading Skills"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Term / Concept (Front)</label>
                  <input
                    type="text"
                    required
                    value={cardTerm}
                    onChange={(e) => setCardTerm(e.target.value)}
                    placeholder="e.g. Phonemic Awareness"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Definition / Key Fact (Back)</label>
                  <textarea
                    rows={3}
                    required
                    value={cardDefinition}
                    onChange={(e) => setCardDefinition(e.target.value)}
                    placeholder="Clear definition or core fact to memorize..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Example / Test Context (Optional)</label>
                  <textarea
                    rows={2}
                    value={cardExample}
                    onChange={(e) => setCardExample(e.target.value)}
                    placeholder="e.g. Asking students to isolate the first sound in 'cat' (/k/)..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <button
                  type="submit"
                  disabled={cardSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>{editingCardId ? 'Update Flashcard' : 'Save Flashcard to Deck'}</span>
                </button>
              </form>
            </div>

            {/* Flashcards List (Right 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-indigo-600" />
                  Flashcards for Paper #{selectedPaperId} ({(existingCards || []).length})
                </h3>
                <button
                  onClick={handleGenerateFlashcardsFromQuestionPool}
                  disabled={cardGenerating}
                  className="bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2 px-4 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50 text-xs"
                >
                  {cardGenerating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  <span>{cardGenerating ? 'Generating...' : 'Generate from Question Pool'}</span>
                </button>
              </div>

              {cardGenError && (
                <div className="p-3 rounded-xl text-xs font-bold flex items-center gap-2 bg-red-50 text-red-800 border border-red-200">
                  <AlertCircle className="w-4 h-4" />
                  <span>{cardGenError}</span>
                </div>
              )}

              {loadingCards ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2, 3].map(i => (
                    <div key={i} className="bg-slate-100 h-20 rounded-xl" />
                  ))}
                </div>
              ) : (existingCards || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <CreditCard className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No flashcards yet — add your first card above</div>
                  <p className="max-w-xs mx-auto">Build high-yield term/definition pairs to power student active recall decks.</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {existingCards.map(fc => (
                    <div key={fc.id} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2 flex flex-col justify-between">
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <span className="px-2 py-0.5 bg-indigo-100 text-indigo-900 font-bold text-[10px] rounded">
                            {fc.domain || 'Core Vocabulary'}
                          </span>
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleEditCardClick(fc)}
                              className="p-1 text-slate-500 hover:text-indigo-600 transition cursor-pointer"
                              title="Edit Flashcard"
                            >
                              <Edit3 className="w-3.5 h-3.5" />
                            </button>
                            <button
                              onClick={() => handleDeleteFlashcard(fc.id)}
                              className="p-1 text-slate-500 hover:text-red-600 transition cursor-pointer"
                              title="Delete Flashcard"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </div>

                        <div className="font-black text-slate-900 text-sm">{fc.term}</div>
                        <p className="text-slate-700 text-xs">{fc.definition}</p>

                        {fc.exampleScenario && (
                          <div className="p-2 bg-white rounded-lg border border-slate-200 text-[11px] text-slate-500 italic">
                            "{fc.exampleScenario}"
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}

        {/* TAB 5: ESSAY PROMPTS MANAGER */}
        {contentTab === 'essays' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Create/Edit Essay Prompt Form (Left) */}
            <div className="lg:col-span-1 bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-200 pb-3">
                <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                  <PenTool className="w-4 h-4 text-indigo-600" />
                  {editingPromptId ? 'Edit Essay Prompt' : 'Create Essay Prompt'}
                </h3>
                {editingPromptId && (
                  <button
                    onClick={handleResetPromptForm}
                    className="text-xs text-slate-500 hover:text-slate-800 font-bold transition cursor-pointer"
                  >
                    Cancel Edit
                  </button>
                )}
              </div>

              {/* Status Alert */}
              {promptStatusMsg && (
                <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                  promptStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                }`}>
                  {promptStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                  <span>{promptStatusMsg.text}</span>
                </div>
              )}

              <form onSubmit={handleSaveEssayPrompt} className="space-y-3.5 text-xs">
                <div>
                  <label className="block font-bold text-slate-700 mb-1">Target Standardized Paper</label>
                  <select
                    value={selectedPaperId}
                    onChange={(e) => setSelectedPaperId(e.target.value)}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    {papers.map(p => (
                      <option key={p.id} value={p.id}>
                        {p.code} - {p.title}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Prompt Type / Genre</label>
                  <select
                    value={promptType}
                    onChange={(e) => setPromptType(e.target.value as 'argumentative' | 'informative-explanatory')}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 cursor-pointer"
                  >
                    <option value="argumentative">Argumentative Task (Position & Counter-Argument)</option>
                    <option value="informative-explanatory">Informative / Explanatory Task (Synthesize & Explain)</option>
                  </select>
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Prompt Title</label>
                  <input
                    type="text"
                    required
                    value={promptTitle}
                    onChange={(e) => setPromptTitle(e.target.value)}
                    placeholder="e.g. Argumentative Essay: Artificial Intelligence in K-12 Classrooms"
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Time Limit (Minutes)</label>
                  <input
                    type="number"
                    min={5}
                    max={120}
                    required
                    value={promptTimeLimit}
                    onChange={(e) => setPromptTimeLimit(Number(e.target.value))}
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Stimulus / Writing Prompt Text</label>
                  <textarea
                    rows={4}
                    required
                    value={promptText}
                    onChange={(e) => setPromptText(e.target.value)}
                    placeholder="Provide the background context, issue, and explicit instructions for what the candidate must argue or explain..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Sample 6/6 Exemplar Response (Optional)</label>
                  <textarea
                    rows={3}
                    value={promptSample6}
                    onChange={(e) => setPromptSample6(e.target.value)}
                    placeholder="Provide a benchmark score 6/6 model essay for reference..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                  />
                </div>

                <div>
                  <label className="block font-bold text-slate-700 mb-1">Rubric Scoring Notes / Guidelines (Optional)</label>
                  <textarea
                    rows={2}
                    value={promptRubricNotes}
                    onChange={(e) => setPromptRubricNotes(e.target.value)}
                    placeholder="e.g. Holistic Score 6/6: Insightful analysis, flawless organization, balanced counter-rebuttal..."
                    className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                  />
                </div>

                <button
                  type="submit"
                  disabled={promptSubmitting}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  <Plus className="w-4 h-4" />
                  <span>{editingPromptId ? 'Update Essay Prompt in Firestore' : 'Save Essay Prompt to Firestore'}</span>
                </button>
              </form>
            </div>

            {/* Essay Prompts List (Right 2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                  <PenTool className="w-5 h-5 text-indigo-600" />
                  Essay Prompts for Paper #{selectedPaperId} ({(existingPrompts || []).length})
                </h3>
              </div>

              {loadingPrompts ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2].map(i => (
                    <div key={i} className="bg-slate-100 h-28 rounded-xl" />
                  ))}
                </div>
              ) : (existingPrompts || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <PenTool className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No essay prompts configured yet for this paper</div>
                  <p className="max-w-xs mx-auto">Add writing tasks (argumentative or informative) to enable full essay simulations and AI holistic evaluations.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {existingPrompts.map((ep, idx) => (
                    <div key={ep.id || idx} className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`px-2 py-0.5 font-bold text-[10px] uppercase rounded ${
                            ep.type === 'argumentative' ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                          }`}>
                            {ep.type === 'argumentative' ? 'Argumentative' : 'Informative / Explanatory'}
                          </span>
                          <span className="px-2 py-0.5 bg-slate-200 text-slate-700 font-mono font-bold text-[10px] rounded">
                            ⏱️ {ep.timeLimitMinutes || 30} mins
                          </span>
                          <span className="text-xs font-mono text-slate-400">ID: {ep.id}</span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleEditPromptClick(ep)}
                            className="p-1 text-slate-500 hover:text-indigo-600 transition cursor-pointer"
                            title="Edit Prompt"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteEssayPrompt(ep.id)}
                            className="p-1 text-slate-500 hover:text-red-600 transition cursor-pointer"
                            title="Delete Prompt"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="font-black text-slate-900 text-sm">{ep.title}</div>

                      <div className="p-3.5 bg-white rounded-lg border border-slate-200 text-xs text-slate-800 leading-relaxed whitespace-pre-wrap">
                        {ep.promptText}
                      </div>

                      {ep.rubricNotes && (
                        <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200/80 text-[11px] text-amber-900">
                          <span className="font-bold block mb-0.5">Rubric Notes:</span>
                          {ep.rubricNotes}
                        </div>
                      )}

                      {ep.sample6Response && (
                        <details className="text-[11px] text-slate-600 bg-white p-2.5 rounded-lg border border-slate-200 cursor-pointer">
                          <summary className="font-bold text-indigo-600 select-none">
                            View Score 6/6 Exemplar Model Response
                          </summary>
                          <p className="mt-2 text-slate-700 leading-relaxed whitespace-pre-wrap pl-2 border-l-2 border-indigo-200">
                            {ep.sample6Response}
                          </p>
                        </details>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
        {/* TAB 6: CONSTRUCTED RESPONSES MANAGER */}
        {contentTab === 'constructedResponses' && (
          <div className="space-y-6">
            {/* Top Toolbar / Mode Toggle */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3">
                <label className="text-xs font-extrabold text-slate-800 whitespace-nowrap">Target Paper:</label>
                <select
                  value={selectedPaperId}
                  onChange={(e) => setSelectedPaperId(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 max-w-[280px]"
                >
                  {papers.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.title}
                    </option>
                  ))}
                </select>
              </div>

              {/* Single vs Bulk Switcher */}
              <div className="flex items-center bg-slate-200/80 p-1 rounded-xl self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => setCrSubMode('single')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                    crSubMode === 'single' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>Single Form</span>
                </button>
                <button
                  type="button"
                  onClick={() => setCrSubMode('bulk')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer flex items-center gap-1.5 ${
                    crSubMode === 'bulk' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>Bulk Import (JSON/CSV)</span>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column: Single Form OR Bulk Importer */}
              <div className="lg:col-span-1 space-y-4">
                {crSubMode === 'single' ? (
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4">
                    <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                      <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                        <FileSignature className="w-4 h-4 text-indigo-600" />
                        {editingCrId ? 'Edit Constructed Response' : 'New Constructed Response'}
                      </h3>
                      {editingCrId && (
                        <button
                          onClick={handleResetCrForm}
                          className="text-xs text-slate-500 hover:text-slate-800 font-bold cursor-pointer"
                        >
                          Cancel Edit
                        </button>
                      )}
                    </div>

                    {/* Status Alert */}
                    {crStatusMsg && (
                      <div className={`p-3 rounded-xl text-xs font-bold flex items-center gap-2 ${
                        crStatusMsg.type === 'success' ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : 'bg-red-50 text-red-800 border border-red-200'
                      }`}>
                        {crStatusMsg.type === 'success' ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
                        <span>{crStatusMsg.text}</span>
                      </div>
                    )}

                    <form onSubmit={handleSaveCrPrompt} className="space-y-3.5 text-xs">
                      <div>
                        <label className="block font-bold text-slate-700 mb-1">
                          Domain / Subtest (Constrained to Parent Paper)
                        </label>
                        <select
                          value={crDomain}
                          onChange={(e) => setCrDomain(e.target.value)}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-semibold focus:ring-2 focus:ring-indigo-500 cursor-pointer shadow-2xs"
                        >
                          {activePaperDomains.map((domName, dIdx) => (
                            <option key={dIdx} value={domName}>
                              {domName}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 mb-1">Time Limit (Minutes)</label>
                        <input
                          type="number"
                          min={5}
                          max={60}
                          required
                          value={crTimeLimit}
                          onChange={(e) => setCrTimeLimit(Number(e.target.value))}
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 mb-1">Constructed Response Prompt Text</label>
                        <textarea
                          rows={5}
                          required
                          value={crPromptText}
                          onChange={(e) => setCrPromptText(e.target.value)}
                          placeholder="Provide the passage/context, specific question prompt, and instructions for what the candidate must synthesize, calculate, or explain..."
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                        />
                      </div>

                      <div>
                        <label className="block font-bold text-slate-700 mb-1">Scoring Rubric & Expected Answer Notes (Optional)</label>
                        <textarea
                          rows={3}
                          value={crRubricNotes}
                          onChange={(e) => setCrRubricNotes(e.target.value)}
                          placeholder="e.g. Full Credit (3-4 pts): Explains phonemic awareness difference, identifies candidate's error in rhyming vs segmenting..."
                          className="w-full bg-white border border-slate-300 rounded-xl px-3 py-2 text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 leading-relaxed"
                        />
                      </div>

                      <button
                        type="submit"
                        disabled={crSubmitting}
                        className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                      >
                        <Plus className="w-4 h-4" />
                        <span>{editingCrId ? 'Update Prompt in Firestore' : 'Save Prompt to Firestore'}</span>
                      </button>
                    </form>
                  </div>
                ) : (
                  /* Bulk Upload Mode */
                  <div className="bg-slate-50 p-5 rounded-2xl border border-slate-200 space-y-4 text-xs">
                    <div className="flex items-center justify-between border-b border-slate-200/80 pb-3">
                      <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                        <Upload className="w-4 h-4 text-indigo-600" />
                        Bulk Import Prompts (JSON / CSV)
                      </h3>
                    </div>

                    {crBulkError && (
                      <div className="p-3 bg-red-50 text-red-800 border border-red-200 rounded-xl font-bold flex items-start gap-2">
                        <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                        <span>{crBulkError}</span>
                      </div>
                    )}

                    <div className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-6 text-center transition bg-white cursor-pointer relative group">
                      <input
                        type="file"
                        accept=".json,.csv"
                        onChange={handleFileUploadForCrBulk}
                        className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                      />
                      <div className="space-y-2">
                        <div className="w-10 h-10 bg-indigo-50 text-indigo-600 rounded-xl flex items-center justify-center mx-auto group-hover:scale-110 transition">
                          <Upload className="w-5 h-5" />
                        </div>
                        <div className="font-bold text-slate-900 text-xs">
                          {crBulkFileName ? crBulkFileName : 'Click or Drag & Drop .json / .csv file'}
                        </div>
                        <p className="text-[11px] text-slate-500">
                          JSON array of objects with <code>promptText</code>, <code>domain</code>, <code>rubricNotes</code>, <code>timeLimitMinutes</code>.
                        </p>
                      </div>
                    </div>

                    {crBulkParsed.length > 0 && (
                      <div className="space-y-3 pt-2">
                        <div className="p-3 bg-emerald-50 text-emerald-900 border border-emerald-200 rounded-xl flex items-center justify-between">
                          <div className="flex items-center gap-2 font-bold">
                            <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            <span>Parsed {crBulkParsed.length} prompts ready to save</span>
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={handleConfirmCrBulkWrite}
                          disabled={crBulkSubmitting}
                          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white font-bold py-2.5 rounded-xl transition shadow-xs cursor-pointer flex items-center justify-center gap-2 disabled:opacity-50"
                        >
                          {crBulkSubmitting ? (
                            <>
                              <Loader2 className="w-4 h-4 animate-spin" />
                              <span>Saving to Firestore...</span>
                            </>
                          ) : (
                            <>
                              <CheckCircle2 className="w-4 h-4" />
                              <span>Confirm & Batch-Save ({crBulkParsed.length})</span>
                            </>
                          )}
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Right Column: Constructed Response Prompts List (2 cols) */}
            <div className="lg:col-span-2 space-y-4">
              <div className="flex items-center justify-between border-b border-slate-100 pb-2">
                <h3 className="font-extrabold text-slate-900 text-base flex items-center gap-2">
                  <FileSignature className="w-5 h-5 text-indigo-600" />
                  Constructed Responses for Paper #{selectedPaperId} ({(existingCrPrompts || []).length})
                </h3>
              </div>

              {loadingCrPrompts ? (
                /* Loading Skeleton */
                <div className="space-y-3 animate-pulse">
                  {[1, 2].map(i => (
                    <div key={i} className="bg-slate-100 h-28 rounded-xl" />
                  ))}
                </div>
              ) : (existingCrPrompts || []).length === 0 ? (
                /* Empty State */
                <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                  <FileSignature className="w-10 h-10 mx-auto text-slate-300" />
                  <div className="font-bold text-slate-600 text-sm">No constructed responses configured yet for this paper</div>
                  <p className="max-w-xs mx-auto">Add domain-specific constructed response prompts to enable short answer / extended response tasks with AI evaluation.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {existingCrPrompts.map((cr, idx) => (
                    <div key={cr.id || idx} className="p-5 bg-slate-50 border border-slate-200 rounded-xl space-y-3">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="px-2 py-0.5 font-bold text-[10px] uppercase rounded bg-indigo-100 text-indigo-800">
                            {cr.domain}
                          </span>
                          <span className="px-2 py-0.5 bg-slate-200 text-slate-700 font-mono font-bold text-[10px] rounded">
                            ⏱️ {cr.timeLimitMinutes || 10} mins
                          </span>
                          <span className="text-xs font-mono text-slate-400">ID: {cr.id}</span>
                        </div>

                        <div className="flex items-center gap-1 shrink-0">
                          <button
                            onClick={() => handleEditCrClick(cr)}
                            className="p-1 text-slate-500 hover:text-indigo-600 transition cursor-pointer"
                            title="Edit Prompt"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteCrPrompt(cr.id)}
                            className="p-1 text-slate-500 hover:text-red-600 transition cursor-pointer"
                            title="Delete Prompt"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>

                      <div className="p-3.5 bg-white rounded-lg border border-slate-200 text-xs text-slate-800 leading-relaxed whitespace-pre-wrap">
                        {cr.promptText}
                      </div>

                      {cr.rubricNotes && (
                        <div className="p-2.5 bg-amber-50 rounded-lg border border-amber-200/80 text-[11px] text-amber-900">
                          <span className="font-bold block mb-0.5">Scoring Rubric & Guidelines:</span>
                          {cr.rubricNotes}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
            </div>
          </div>
        )}
        {/* TAB 7: PERFORMANCE TASKS MANAGER (SHELL / ROUTING TARGET) */}
        {contentTab === 'performanceTasks' && (
          <div className="space-y-6">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <div className="flex items-center gap-3">
                <label className="text-xs font-extrabold text-slate-800 whitespace-nowrap">Target Assessment:</label>
                <select
                  value={selectedPaperId}
                  onChange={(e) => setSelectedPaperId(e.target.value)}
                  className="bg-white border border-slate-300 rounded-xl px-3 py-1.5 text-xs font-bold text-slate-900 focus:ring-2 focus:ring-indigo-500 max-w-[280px]"
                >
                  {papers.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.title} ({p.contentType || 'standard'})
                    </option>
                  ))}
                </select>
              </div>
              <button
                onClick={() => setContentTab('papers')}
                className="px-3 py-1.5 bg-white hover:bg-slate-100 text-slate-700 font-bold text-xs border border-slate-300 rounded-xl transition"
              >
                ← Back to Papers
              </button>
            </div>

            <div className="p-8 bg-sky-50/50 rounded-2xl border border-sky-200 text-center space-y-4">
              <div className="w-12 h-12 bg-sky-100 text-sky-700 rounded-2xl flex items-center justify-center mx-auto shadow-2xs">
                <Layers className="w-6 h-6" />
              </div>
              <div className="space-y-1 max-w-xl mx-auto">
                <h3 className="font-extrabold text-slate-900 text-base">Performance Tasks & Rubrics Manager</h3>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Portfolio-based performance assessment for <strong>{activeSelectedPaper?.title || selectedPaperId}</strong>.
                  Candidate portfolio tasks, rubric scoring matrices, and commentary prompts will be managed here in the next build phase.
                </p>
              </div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white text-sky-900 font-bold text-xs rounded-xl border border-sky-200 shadow-2xs">
                <CheckCircle2 className="w-4 h-4 text-sky-600" />
                <span>Architecture Shell Ready: {activeSelectedPaper?.performanceTasks?.length || 0} Registered Tasks</span>
              </div>
            </div>
          </div>
        )}

        {/* TAB 8: REGISTERED USERS MANAGER */}
        {contentTab === 'users' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                <Users className="w-4 h-4 text-indigo-600" />
                Registered Users ({(filteredUsers || []).length} of {(users || []).length})
              </h3>
              <div className="relative w-full sm:w-80">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  value={userSearchQuery}
                  onChange={(e) => setUserSearchQuery(e.target.value)}
                  placeholder="Search by name, email, or UID..."
                  className="w-full bg-white border border-slate-300 rounded-xl pl-9 pr-3 py-2 text-xs text-slate-900 font-medium focus:ring-2 focus:ring-indigo-500 focus:outline-none"
                />
              </div>
            </div>

            {loading ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-slate-100 h-16 rounded-xl" />
                ))}
              </div>
            ) : (filteredUsers || []).length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                <Users className="w-10 h-10 mx-auto text-slate-300" />
                <div className="font-bold text-slate-600 text-sm">No matching users found</div>
                <p className="max-w-xs mx-auto">Try a different name, email, or UID search term.</p>
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl bg-white">
                <table className="w-full text-left border-collapse text-xs whitespace-nowrap">
                  <thead>
                    <tr className="bg-slate-100 border-b border-slate-200 font-bold text-slate-600">
                      <th className="p-2.5">Name</th>
                      <th className="p-2.5">Email</th>
                      <th className="p-2.5">Tier</th>
                      <th className="p-2.5">Full Access</th>
                      <th className="p-2.5">Unlocked Papers</th>
                      <th className="p-2.5">Subscription</th>
                      <th className="p-2.5">Target State</th>
                      <th className="p-2.5">Streak</th>
                      <th className="p-2.5">Study Minutes</th>
                      <th className="p-2.5">Joined</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filteredUsers.map((u) => {
                      const isAdminUser = u?.role === 'admin' || u?.isAdmin === true;
                      return (
                        <tr key={u.uid} className="hover:bg-slate-50">
                          <td className="p-2.5 font-bold text-slate-900">
                            <div className="flex items-center gap-1.5">
                              <span>{u.displayName || 'Unnamed User'}</span>
                              {isAdminUser && (
                                <span className="px-1.5 py-0.5 bg-amber-500 text-slate-950 font-black text-[9px] rounded uppercase tracking-wider">
                                  Admin
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="p-2.5 text-slate-700">{u.email}</td>
                          <td className="p-2.5">
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-800 font-bold text-[10px] rounded uppercase">
                              {u.tier || 'free'}
                            </span>
                          </td>
                          <td className="p-2.5">
                            {u.hasFullAccess ? (
                              <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold text-[10px] rounded uppercase">Yes</span>
                            ) : (
                              <span className="px-2 py-0.5 bg-slate-100 text-slate-500 font-bold text-[10px] rounded uppercase">No</span>
                            )}
                          </td>
                          <td className="p-2.5 font-semibold text-slate-700">{u.unlockedPaperIds?.length ?? 0}</td>
                          <td className="p-2.5 text-slate-700">
                            {u.subscriptionStatus ? (
                              <div className="flex flex-col">
                                <span className="font-bold">{u.subscriptionStatus}</span>
                                {u.subscriptionEndDate && (
                                  <span className="text-[10px] text-slate-400">Ends {u.subscriptionEndDate}</span>
                                )}
                              </div>
                            ) : (
                              <span className="text-slate-400 italic">None</span>
                            )}
                          </td>
                          <td className="p-2.5 text-slate-700">{u.targetState || '—'}</td>
                          <td className="p-2.5">
                            <span className="inline-flex items-center gap-1 font-bold text-amber-700">
                              <Flame className="w-3.5 h-3.5 text-amber-500" />
                              {u.streakDays ?? 0}
                            </span>
                          </td>
                          <td className="p-2.5 font-semibold text-slate-700">{u.totalStudyMinutes ?? 0}</td>
                          <td className="p-2.5 text-slate-500">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* TAB 9: TUTORING BOOKINGS MANAGER */}
        {contentTab === 'tutoringBookings' && (
          <div className="space-y-4">
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 p-4 bg-slate-50 rounded-2xl border border-slate-200">
              <h3 className="font-extrabold text-slate-900 text-sm flex items-center gap-2">
                <GraduationCap className="w-4 h-4 text-indigo-600" />
                Tutoring Bookings ({(filteredTutoringBookings || []).length} of {(tutoringBookings || []).length})
              </h3>

              <div className="flex items-center bg-slate-200/80 p-1 rounded-xl self-start sm:self-auto">
                <button
                  type="button"
                  onClick={() => setTutoringBookingsFilter('all')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    tutoringBookingsFilter === 'all' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  All
                </button>
                <button
                  type="button"
                  onClick={() => setTutoringBookingsFilter('session')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    tutoringBookingsFilter === 'session' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Paid Sessions
                </button>
                <button
                  type="button"
                  onClick={() => setTutoringBookingsFilter('consultation')}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition cursor-pointer ${
                    tutoringBookingsFilter === 'consultation' ? 'bg-white text-indigo-700 shadow-xs' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  Consultation Requests
                </button>
              </div>
            </div>

            {loadingTutoringBookings ? (
              <div className="space-y-3 animate-pulse">
                {[1, 2, 3].map(i => (
                  <div key={i} className="bg-slate-100 h-16 rounded-xl" />
                ))}
              </div>
            ) : (filteredTutoringBookings || []).length === 0 ? (
              <div className="py-12 text-center text-slate-400 text-xs bg-slate-50 rounded-2xl border border-dashed border-slate-200 space-y-2">
                <GraduationCap className="w-10 h-10 mx-auto text-slate-300" />
                <div className="font-bold text-slate-600 text-sm">No tutoring bookings found</div>
                <p className="max-w-xs mx-auto">Paid session requests and consultation requests submitted by students will appear here.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredTutoringBookings.map((b) => {
                  const isConsultation = b.requestType === 'consultation';
                  return (
                    <div key={b.id} className="p-4 bg-white border border-slate-200 rounded-xl space-y-2 text-xs">
                      <div className="flex items-center justify-between flex-wrap gap-2">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`px-2 py-0.5 font-bold text-[10px] uppercase rounded ${
                            isConsultation ? 'bg-sky-100 text-sky-800' : 'bg-emerald-100 text-emerald-800'
                          }`}>
                            {isConsultation ? 'Consultation Request' : 'Paid Session'}
                          </span>
                          <span className="px-2 py-0.5 bg-indigo-50 text-indigo-800 font-bold text-[10px] rounded uppercase">
                            Paper {b.paperCode}
                          </span>
                          {!isConsultation && (b.date || b.time) && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-700 font-mono font-bold text-[10px] rounded">
                              {b.date} {b.time}
                            </span>
                          )}
                          {b.tutorName && (
                            <span className="text-[11px] text-slate-500">with {b.tutorName}</span>
                          )}
                        </div>
                        <span className={`px-2 py-0.5 font-bold text-[10px] rounded uppercase ${
                          b.status === 'Confirmed' ? 'bg-emerald-100 text-emerald-800' :
                          b.status === 'Completed' ? 'bg-indigo-100 text-indigo-800' : 'bg-amber-100 text-amber-800'
                        }`}>
                          {b.status}
                        </span>
                      </div>

                      <div className="p-2.5 bg-slate-50 rounded-lg border border-slate-200 text-slate-700 flex items-start gap-2">
                        <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0 mt-0.5" />
                        <span className="whitespace-pre-wrap">{b.topic}</span>
                      </div>

                      <div className="text-[11px] text-slate-400">
                        Submitted: {b.createdAt ? new Date(b.createdAt).toLocaleString() : '—'}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};